import { Link } from "react-router-dom"
import { SiteHeader } from "@/components/site-header"
import { REPORT_GEO_IMPORT_PATH } from "@/lib/report-paths"
import { Badge } from "@/components/ui/badge"
import { TrendingUpIcon, WrenchIcon, FileTextIcon } from "lucide-react"

const entries = [
  {
    version: "v0.113.0",
    date: "2026-10-27",
    sections: [
      {
        title: "Dashboard — kilómetros asociados a cargas",
        type: "feat" as const,
        items: [
          "La tabla de Cargas y Viajes ahora muestra debajo de cada cantidad de cargas los kilómetros acumulados de sus viajes asociados.",
          "La fila Total también incluye los kilómetros correspondientes a viajes con carga.",
        ],
      },
    ],
  },
  {
    version: "v0.112.0",
    date: "2026-10-27",
    sections: [
      {
        title: "Dashboard — estabilidad y rendimiento",
        type: "fix" as const,
        items: [
          "El dashboard ya no se rompe si la respuesta remota de estadísticas trae métricas faltantes o con otro formato numérico.",
          "Las estadísticas de operaciones se almacenan temporalmente en la API para evitar recalcular kilómetros en cada carga de la pantalla.",
        ],
      },
    ],
  },
  {
    version: "v0.111.0",
    date: "2026-10-27",
    sections: [
      {
        title: "Dashboard — kilómetros por línea de trabajo",
        type: "feat" as const,
        items: [
          "La tabla de Cargas y Viajes ahora muestra debajo de cada cantidad de viajes los kilómetros acumulados del período, con un estilo más pequeño y gris.",
          "La fila Total también incluye el total de kilómetros acumulados.",
        ],
      },
    ],
  },
  {
    version: "v0.110.0",
    date: "2026-10-27",
    sections: [
      {
        title: "Viajes — cambio de recursos históricos",
        type: "fix" as const,
        items: [
          "Al editar un viaje ahora se pueden cambiar explícitamente el chofer, el camión y el semi sin modificar el móvil asignado.",
          "El semi y el camión históricos siguen apareciendo en los selectores aunque ya no estén en el listado activo o tengan un tipo legado.",
        ],
      },
    ],
  },
  {
    version: "v0.109.0",
    date: "2026-10-23",
    sections: [
      {
        title: "Cargas nacionales — rutas y paradas",
        type: "feat" as const,
        items: [
          "La edición de rutas y paradas nacionales ahora funciona igual desde el listado y desde el detalle de la carga.",
          "El detalle de una carga nacional muestra las paradas guardadas en modo consulta.",
          "Al cambiar una carga nacional a otra línea de trabajo se limpian automáticamente la ruta y las paradas nacionales anteriores.",
          "Los datos de viaje mantienen sus kilómetros; los datos de carga ya no muestran kilómetros en el bloque informativo.",
        ],
      },
    ],
  },
  {
    version: "v0.108.0",
    date: "2026-10-22",
    sections: [
      {
        title: "Cargas — búsqueda por ID externo de partes",
        type: "feat" as const,
        items: [
          "Los selectores de remitente y destinatario ahora permiten buscar por el ID externo de la parte.",
          "El ID externo se muestra junto al nombre en los resultados de búsqueda cuando está disponible.",
        ],
      },
    ],
  },
  {
    version: "v0.107.0",
    date: "2026-10-22",
    sections: [
      {
        title: "Partes — identificador externo",
        type: "feat" as const,
        items: [
          "Las partes ahora tienen un ID externo separado del nombre para guardar el número de tienda utilizado por el cliente.",
          "Los identificadores de cuatro dígitos existentes se separan del nombre: por ejemplo, 1005 Las Heras queda como ID externo 1005 y nombre Las Heras.",
        ],
      },
    ],
  },
  {
    version: "v0.106.0",
    date: "2026-10-22",
    sections: [
      {
        title: "Exportación de facturación — selección entre páginas",
        type: "fix" as const,
        items: [
          "Las cargas seleccionadas en distintas páginas de la paginación ahora se incluyen correctamente en la exportación CSV y XLS.",
        ],
      },
    ],
  },
  {
    version: "v0.105.0",
    date: "2026-10-21",
    sections: [
      {
        title: "Partes — búsqueda y paginación",
        type: "fix" as const,
        items: [
          "La búsqueda de partes ahora consulta todos los registros y no queda limitada a la primera página.",
          "La lista de partes ahora pagina y aplica los filtros desde el servidor, mejorando los resultados cuando hay muchos registros.",
        ],
      },
    ],
  },
  {
    version: "v0.104.0",
    date: "2026-10-21",
    sections: [
      {
        title: "Cargas internacionales — informe al cliente",
        type: "feat" as const,
        items: [
          "Se agregó el export \"Informe al cliente\" para cargas de línea internacional, con cliente, factura comercial, CRT, plantas, chofer y los hitos del viaje.",
          "El informe incluye las estadías por etapa, calculadas con la misma lógica que se muestra en el detalle de la carga.",
        ],
      },
    ],
  },
  {
    version: "v0.103.0",
    date: "2026-10-21",
    sections: [
      {
        title: "Tipos de documentos de viaje",
        type: "feat" as const,
        items: [
          "Se agregó un catálogo administrable para los tipos de documentos de viaje, con código, nombre y control.",
          "Los documentos nuevos sin tipo se registran como Remito y se conservaron los documentos históricos existentes.",
        ],
      },
    ],
  },
  {
    version: "v0.102.0",
    date: "2026-10-05",
    sections: [
      {
        title: "Cargas nacionales — trayectorias y detalle de paradas",
        type: "feat" as const,
        items: [
          "Las trayectorias de las cargas nacionales ahora se seleccionan desde Geografía → Ciudades y Trayectorias, con búsqueda por código o nombre.",
          "Cuando una carga tiene al menos una parada intermedia, la trayectoria pasa a ser obligatoria para poder guardarla.",
          "El KM de la carga se calcula usando la ruta entre la ciudad de origen y la trayectoria seleccionada; si no existe, se utiliza como respaldo la ruta directa entre origen y destino.",
          "Las cargas con múltiples paradas muestran un ícono azul junto al kilometraje. Al pasar el mouse se pueden consultar todas las paradas y la ruta utilizada, incluyendo código, origen, destino y KM.",
          "Al editar una carga, las paradas actualizadas se reflejan inmediatamente en el detalle emergente sin tener que recargar la página.",
        ],
      },
    ],
  },
  {
    version: "v0.101.0",
    date: "2026-10-02",
    sections: [
      {
        title: "Paradas y viajes en cargas nacionales",
        type: "feat" as const,
        items: [
          "El creador de cargas nacionales permite agregar paradas intermedias entre parties, incluyendo recorridos de ida y vuelta al mismo party.",
          "Cada conexión entre paradas puede marcarse individualmente para generar o no un viaje.",
          "La trayectoria es obligatoria cuando la carga tiene al menos una parada intermedia y su KM se utiliza como referencia del recorrido completo.",
          "Los viajes generados conservan la relación con sus paradas de origen y destino y reciben los recursos del móvil seleccionado.",
        ],
      },
    ],
  },
  {
    version: "v0.100.0",
    date: "2026-10-02",
    sections: [
      {
        title: "IDs internacionales — correcciones y mejoras",
        type: "fix" as const,
        items: [
          "La consulta del máximo ID asignado en la base de datos ahora funciona correctamente: antes retornaba vacío y se volvía a sugerir la semilla inicial del corredor, produciendo un error de duplicado al guardar.",
          "Al editar una carga, el botón \"Agregar siguiente\" propone el mayor ID entre el cálculo local y el valor confirmado por el servidor, evitando sugerir un ID ya asignado a otra carga.",
          "Al crear una carga, el formulario muestra el ID que el servidor reservará automáticamente en lugar de intentar completar uno igual al ya existente.",
          "Guardar con ID borrado y edición de móvil al mismo tiempo ya no descarta los cambios de chofer o acoplado.",
          "La validación al editar configuraciones de corredores rechaza semillas con prefijos duplicados o formato inválido.",
          "Los errores de formato de ID devuelven 422 en lugar de 409 para distinguirlos de los conflictos con otras cargas.",
          "Los cambios en IDs internacionales quedan registrados en el historial de auditoría junto con la carga.",
        ],
      },
    ],
  },
  {
    version: "v0.99.0",
    date: "2026-10-02",
    sections: [
      {
        title: "IDs internacionales — generación y corredores",
        type: "feat" as const,
        items: [
          "Las cargas internacionales reciben IDs únicos por corredor (origen → destino) configurados desde Sistema → Configuraciones.",
          "Al crear una carga, el sistema reserva el siguiente ID disponible de forma automática y transaccional, evitando duplicados bajo carga simultánea.",
          "Es posible ingresar IDs manualmente; el sistema valida el prefijo y el largo del corredor y actualiza la secuencia para que los valores futuros no colisionen.",
          "Eliminar IDs existentes requiere confirmación explícita. El corredor detecta automáticamente cambios de origen o destino y limpia los IDs previos.",
          "Las cargas Batea no reciben ni alteran IDs internacionales.",
          "El detalle y el formulario de cargas comparten el mismo componente editor.",
        ],
      },
    ],
  },
  {
    version: "v0.98.0",
    date: "2026-10-02",
    sections: [
      {
        title: "Monitoreo de errores en producción",
        type: "fix" as const,
        items: [
          "El monitoreo Sentry del API ahora se activa únicamente en producción cuando existe una configuración válida, evitando reportes accidentales desde los entornos locales.",
        ],
      },
    ],
  },
  {
    version: "v0.97.0",
    date: "2026-10-02",
    sections: [
      {
        title: "Actualización inmediata de configuraciones",
        type: "fix" as const,
        items: [
          "Los cambios realizados desde Sistema → Configuraciones ahora se aplican inmediatamente, sin esperar la renovación del caché de la API.",
        ],
      },
    ],
  },
  {
    version: "v0.96.0",
    date: "2026-10-02",
    sections: [
      {
        title: "Configuraciones administrables",
        type: "feat" as const,
        items: [
          "Se agregó una tabla de configuraciones para almacenar nuevos valores sin modificar inicialmente la configuración existente en app.config.json.",
          "El menú Sistema → Configuraciones permite consultar y modificar los valores de las claves creadas mediante seeder.",
          "Durante la etapa de transición, los valores de la tabla tienen prioridad y app.config.json permanece como fallback para las claves todavía no migradas.",
          "Las nuevas claves se agregan por seeder; la pantalla no permite crear claves nuevas todavía.",
          "La tabla de configuraciones adapta los valores JSON y las descripciones largas sin generar desplazamiento horizontal en la página.",
        ],
      },
      {
        title: "IDs internacionales por corredor",
        type: "feat" as const,
        items: [
          "Se agregaron los IDs externos por corredor para las cargas de línea Internacional (AR-UY, UY-AR, AR-PY, PY-AR, AR-CL, CL-AR, AR-BR y BR-AR).",
        ],
      },
      {
        title: "Exportación de facturación — responsables de camiones",
        type: "feat" as const,
        items: [
          "La exportación de facturación ahora incluye el operador asignado a cada unidad camión que participó en la carga.",
          "Cuando una carga utiliza varios camiones, se incluyen todos los operadores asignados a esas unidades.",
        ],
      },
    ],
  },
  {
    version: "v0.95.0",
    date: "2026-10-02",
    sections: [
      {
        title: "Exportación de facturación — comentarios multilínea",
        type: "fix" as const,
        items: [
          "Los comentarios que contienen saltos de línea ya no rompen la vista previa ni generan filas adicionales al descargar el CSV o XLS.",
          "La importación y exportación de facturación ahora respetan correctamente los campos CSV con comas, comillas y saltos de línea.",
        ],
      },
    ],
  },
  {
    version: "v0.94.0",
    date: "2026-10-02",
    sections: [
      {
        title: "Facturación de cargas — tabla y filtros ampliados",
        type: "feat" as const,
        items: [
          "La tabla y la exportación ahora incluyen cliente, choferes, móvil, patentes, remito con estado de validación, toneladas descargadas y observaciones de carga.",
          "Se puede cambiar masivamente el estado de facturación de las cargas seleccionadas, incluyendo volver a Pendiente; el cambio también se propaga a sus viajes.",
          "Los filtros de facturación y estado de remito permiten seleccionar múltiples estados al mismo tiempo.",
        ],
      },
    ],
  },
  {
    version: "v0.93.0",
    date: "2026-09-25",
    sections: [
      {
        title: "Importación de facturación — soporte Excel (.xlsx / .xls)",
        type: "feat" as const,
        items: [
          "El diálogo de importación de facturación ahora acepta archivos .xlsx y .xls además de .csv.",
          "El archivo de ejemplo descargable se genera como .xlsx con columnas preformateadas (ancho ajustado, hoja nombrada).",
          "La librería SheetJS se carga de forma lazy solo al abrir el diálogo, sin impacto en el bundle inicial.",
        ],
      },
      {
        title: "Export de facturación — paginación completa",
        type: "fix" as const,
        items: [
          "\"Exportar todo\" en Facturación de cargas ahora obtiene todos los registros del servidor en páginas de 500 filas, evitando el error 400 que ocurría con más de 500 resultados.",
          "Límite de 2000 filas por export: si hay más resultados se muestra un aviso y se sugiere acotar con filtros o usar selección manual.",
        ],
      },
    ],
  },
  {
    version: "v0.92.0",
    date: "2026-09-24",
    sections: [
      {
        title: "Importación masiva de facturación",
        type: "feat" as const,
        items: [
          "Nuevo botón \"Importar facturación\" en Cargas → Facturación: permite subir un CSV con IDs de carga, observaciones y etiquetas.",
          "Vista previa antes de confirmar: muestra cada carga encontrada (con su estado actual de facturación) y resalta en rojo las no encontradas. Es obligatorio eliminar las filas erróneas antes de poder confirmar.",
          "Soporte de archivos con separador coma o tabulación (TSV/Excel exportado). El separador se detecta automáticamente.",
          "Las filas con encabezados de texto (ej: \"ID\", \"DATOS DE CARGAS\") se saltan automáticamente, sin necesidad de preparar el archivo.",
          "Actualización masiva opcional: al tildar la opción se puede aplicar un mismo estado de facturación, observaciones y/o etiquetas a todas las cargas del archivo.",
          "Archivo de ejemplo descargable directamente desde el diálogo.",
          "El cambio de estado de facturación se propaga automáticamente a todos los viajes asociados a cada carga.",
          "Las filas con error aparecen siempre primero en la tabla de preview. La tabla pagina de a 50 filas para manejar archivos grandes.",
          "Límite de 1000 filas por importación con mensaje claro si se supera.",
          "Instrucciones de uso visibles directamente en el diálogo.",
        ],
      },
    ],
  },
  {
    version: "v0.91.0",
    date: "2026-09-24",
    sections: [
      {
        title: "Facturación de cargas — nueva vista dedicada",
        type: "feat" as const,
        items: [
          "Nueva sección Facturación en el menú de Cargas (/shipments/billing): vista orientada al área de facturación con columnas específicas (Fecha, Facturación, Etiquetas, Obs. facturación).",
          "Filtros propios: estado operativo (chips), estado de facturación, cliente, línea de trabajo, operadores, etiqueta y rango de fechas. Por defecto muestra solo cargas Finalizadas.",
          "Edición rápida de etiquetas y observaciones de facturación con click directo en la celda, sin abrir el formulario completo.",
          "Autocomplete de etiquetas al escribir 2 o más letras en el filtro de etiqueta.",
          "Export CSV/XLS: sin selección exporta todos los registros del servidor (hasta 2000, con aviso si se supera el límite); con selección exporta solo las cargas elegidas.",
          "El buscador principal incluye búsqueda por N° de carga, observaciones de facturación y etiquetas.",
        ],
      },
      {
        title: "Etiquetas de facturación en cargas",
        type: "feat" as const,
        items: [
          "Nuevo campo Etiquetas de facturación en cargas: texto libre múltiple (ej: Lote 1, #212). Se agregan presionando Enter o coma y se eliminan con el botón ×.",
          "Las etiquetas se copian automáticamente al duplicar una carga.",
          "El buscador de cargas incluye búsqueda por etiqueta vía subconsulta EXISTS, junto con remito, N° de carga, observaciones de facturación y otros campos.",
        ],
      },
    ],
  },
  {
    version: "v0.90.0",
    date: "2026-09-24",
    sections: [
      {
        title: "Dividir viaje — mejoras en UX",
        type: "feat" as const,
        items: [
          "El botón Dividir viaje ahora aparece directamente en la fila del viaje (ícono tijera), fuera del menú de tres puntos.",
          "Los selectores de Camión y Semi en el diálogo de división buscan en el servidor al escribir 2 o más letras, mostrando resultados completos en lugar de la primera página del listado.",
          "La búsqueda de unidades ahora incluye el número de unidad (ej: #244) además de patente, código y nombre.",
          "El selector de Camión filtra solo unidades de tipo Camión/Tractor y TractorGNC; el de Semi filtra solo Semi, Batea, Bivuelco y Playo.",
          "Cada resultado muestra el nombre completo (Marca Modelo) en la primera línea y la patente, tipo y año en la segunda.",
        ],
      },
    ],
  },
  {
    version: "v0.89.0",
    date: "2026-09-24",
    sections: [
      {
        title: "SmartGT — DESCRIPCION con todos los CRTs del viaje",
        type: "feat" as const,
        items: [
          "En el export de datos de carga, cuando varios CRTs del mismo chofer y cliente se colapsan en una sola fila (Regla 5), la columna DESCRIPCION ahora muestra todos los números de CRT separados por ' / ' (ej: BR180917777 / BR180917793) en lugar de mostrar solo el primero.",
        ],
      },
    ],
  },
  {
    version: "v0.88.0",
    date: "2026-09-24",
    sections: [
      {
        title: "SmartGT — exports fleteros mejorados",
        type: "feat" as const,
        items: [
          "En el export de datos de viaje, los CRTs con carrier CDC y propietario fletero externo (prefijos AR180, BR180, AR244, BR244, PY611, AR611) ahora exportan las columnas CAMION y SEMI vacías, además de reemplazar NOMBRE CHOFER / PROV NRO / CHOFER por FLETEROS VARIOS.",
          "La fecha de coleta en los exports de datos de carga, documentos y valorización ahora usa la fecha de salida de coleta como respaldo cuando la llegada a coleta no está registrada, evitando filas sin fecha.",
          "La columna de llegada a coleta en el listado principal y en el modal de detalle también aplica este respaldo, mostrando siempre la mejor fecha disponible.",
        ],
      },
    ],
  },
  {
    version: "v0.87.0",
    date: "2026-09-24",
    sections: [
      {
        title: "Cargas — campo Ruteado",
        type: "feat" as const,
        items: [
          "Nuevo campo booleano Ruteado en cada carga: indica que el cliente confirmó el camión y chofer asignados.",
          "En la tabla de cargas aparece como un ícono de ruta al lado del ícono de observaciones: verde si está ruteado, gris si no. Click para alternar sin abrir el formulario.",
          "En el kanban, las tarjetas de estado En planificación y Planificado muestran el mismo ícono en el footer al pasar el mouse, para marcar o quitar el ruteado sin salir del kanban.",
          "Nuevo filtro Ruteado en el kanban: permite ver solo cargas ruteadas, solo no ruteadas, o todas. El filtro se procesa en el servidor y se recuerda al recargar la página.",
          "El campo está disponible en el formulario de creación y edición, tanto en la tabla (sheet lateral) como en el detalle de carga.",
        ],
      },
      {
        title: "Facturación — nuevo estado Controlado",
        type: "feat" as const,
        items: [
          "Se agregó el estado de facturación Controlado entre Pendiente y Liquidado, disponible en cargas y viajes.",
          "Se muestra con badge teal en la tabla, el kanban y el detalle de viajes.",
          "Disponible en el filtro de facturación, el cambio por fila y el cambio masivo desde la barra de selección.",
        ],
      },
    ],
  },
  {
    version: "v0.86.0",
    date: "2026-09-24",
    sections: [
      {
        title: "Viajes y Cargas — filtros mejorados",
        type: "feat" as const,
        items: [
          "Nuevo filtro por operador en Viajes: selector múltiple con búsqueda, igual al de Cargas. Los operadores se cargan completos desde el servidor (no solo los de la página visible).",
          "Nuevo filtro «Con/sin carga» en Viajes: permite ver solo viajes asociados a una carga, solo viajes libres, o todos.",
          "El filtro de chofer en Viajes y Cargas ahora usa búsqueda en tiempo real (mínimo 3 letras). El chofer seleccionado se recuerda al recargar la página.",
          "Los filtros de operador, chofer y cliente tienen ahora el mismo estilo visual (mismo alto, mismo fondo transparente) que los selectores de estado y facturación.",
          "La paginación aparece también arriba de la tabla en Viajes y Cargas para navegar sin tener que bajar al pie.",
          "Los filtros están organizados visualmente en grupos separados por líneas verticales: estado — personas — facturación — fechas.",
        ],
      },
      {
        title: "Cargas — columna Chofer ampliada",
        type: "feat" as const,
        items: [
          "La patente del semi aparece al lado de la del camión en la columna Chofer, con su propio botón de copiar independiente.",
          "El indicador «A» (verde con anticipo / gris sin anticipo) aparece junto al nombre del chofer y se actualiza automáticamente cuando se modifica el anticipo desde la tabla de viajes.",
        ],
      },
      {
        title: "Selector múltiple unificado",
        type: "feat" as const,
        items: [
          "El componente SearchableSelect ahora soporta selección múltiple (prop `multiple`) además de la selección simple. El componente separado MultiSelect fue eliminado.",
          "En modo múltiple muestra checkboxes, contador de seleccionados y enlace «Limpiar selección» en el pie del dropdown.",
          "En modo simple con `emptyValue`, muestra «Limpiar selección» cuando hay un valor activo.",
        ],
      },
    ],
  },
  {
    version: "v0.85.0",
    date: "2026-09-23",
    sections: [
      {
        title: "Cargas — edición rápida desde la tabla",
        type: "feat" as const,
        items: [
          "Hacé click en el remitente o el destinatario de cualquier carga para cambiarlos sin abrir el formulario completo.",
          "Hacé click en el chofer para reasignar el móvil completo (chofer, camión y semi se actualizan en un solo paso).",
          "En cargas batea, la ventana de carga aparece debajo de la fecha de salida y también es clickeable para modificarla.",
          "Debajo del nombre del chofer se muestra la patente del camión con un botón de copiar que aparece al pasar el cursor.",
          "Todos los cambios se guardan con animación de apertura y se reflejan inmediatamente en la tabla.",
        ],
      },
    ],
  },
  {
    version: "v0.84.0",
    date: "2026-09-18",
    sections: [
      {
        title: "Conceptos de pago — edición disponible para operadores",
        type: "feat" as const,
        items: [
          "El rol Operador ahora puede crear, editar, eliminar y restaurar conceptos de pago.",
          "Al editar un concepto, la tarifa unitaria se muestra con 2 decimales y la moneda se visualiza correctamente en el formulario.",
        ],
      },
    ],
  },
  {
    version: "v0.83.0",
    date: "2026-09-18",
    sections: [
      {
        title: "Exportación de valorización — precio por km corregido",
        type: "fix" as const,
        items: [
          "Las columnas PRECIO en los exports de valorización (desde Viajes y desde Embarques) ahora muestran la tarifa unitaria por km, no el total multiplicado por km. Esto refleja el formato esperado por el sistema de liquidación.",
        ],
      },
      {
        title: "App chofer web — subida de fotos sin error de memoria",
        type: "fix" as const,
        items: [
          "La subida de fotos de remito en la versión web de la app del chofer ahora transmite la imagen directamente al servidor sin cargarla completa en memoria, eliminando el error \"Memoria insuficiente para completar la operación anterior\" en Chrome en dispositivos de gama baja.",
        ],
      },
    ],
  },
  {
    version: "v0.82.0",
    date: "2026-09-17",
    sections: [
      {
        title: "Cargas — filtro de chofer con búsqueda server-side",
        type: "fix" as const,
        items: [
          "El filtro de chofer en la página de Cargas ahora busca contra la API al escribir 3 o más letras, en lugar de mostrar solo los choferes de la página visible.",
          "El desplegable indica 'Escribí 3 letras para buscar' hasta alcanzar el mínimo, y muestra hasta 50 resultados coincidentes.",
        ],
      },
    ],
  },
  {
    version: "v0.81.0",
    date: "2026-09-17",
    sections: [
      {
        title: "Exportación de datos de viaje — código de unidad en lugar de patente",
        type: "fix" as const,
        items: [
          "Las columnas CAMION (12) y SEMI (13) en los exports 'Datos de viaje' ahora muestran el código interno de la unidad en lugar de la patente, tanto en el export desde Viajes como desde Embarques.",
        ],
      },
    ],
  },
  {
    version: "v0.80.0",
    date: "2026-09-17",
    sections: [
      {
        title: "Unidades — filtro de operadores server-side",
        type: "fix" as const,
        items: [
          "El filtro de operadores en la página de Unidades ahora se procesa en el servidor, por lo que filtra correctamente sobre el total de registros y no solo sobre la página visible.",
          "Los chips de operadores se cargan desde la API de usuarios (operadores y fleet managers) en lugar de derivarse de la página actual, por lo que aparecen todos los operadores aunque no tengan unidades en la página activa.",
          "Nuevo componente reutilizable <Multiselect> que permite selección múltiple con búsqueda, chips con contador +N y limpieza individual — disponible para cualquier filtro que lo requiera.",
        ],
      },
    ],
  },
  {
    version: "v0.79.0",
    date: "2026-09-17",
    sections: [
      {
        title: "Viajes — soporte de llegada proyectada en viaje de interconexión y regreso a base",
        type: "feat" as const,
        items: [
          "El viaje de interconexión ahora acepta la llegada proyectada a origen (Ll. proy. origen) como fecha de referencia cuando el viaje no tiene fecha de salida registrada. La fecha intermedia se calcula con la misma lógica.",
          "El viaje de regreso a base también acepta la llegada proyectada como tercer fallback para calcular la fecha de salida del viaje de regreso.",
          "Los mensajes de bloqueo en ambos diálogos se actualizaron para reflejar los tres campos de fecha posibles.",
        ],
      },
      {
        title: "Login — normalización del email",
        type: "fix" as const,
        items: [
          "El email se normaliza automáticamente (trim + minúsculas) en el navegador y en el servidor al iniciar sesión, evitando errores de autenticación por espacios o mayúsculas accidentales.",
        ],
      },
      {
        title: "Seeder de actualización de choferes — mejoras de robustez",
        type: "fix" as const,
        items: [
          "El seeder detecta y omite cambios de código que colisionarían con otro chofer (activo o eliminado), evitando errores de restricción UNIQUE en la base de datos.",
          "Compatible con Neon pooler: el seeder detecta automáticamente la URL del pooler y cambia al endpoint directo para que los comandos SET funcionen correctamente.",
          "La variable de entorno RUN=1 reemplaza al flag --run para mayor compatibilidad con Turbo y otras herramientas CI.",
        ],
      },
    ],
  },
  {
    version: "v0.78.0",
    date: "2026-09-16",
    sections: [
      {
        title: "Viajes — filtro mejorado por llegada programada a origen con ordenamiento avanzado",
        type: "feat" as const,
        items: [
          "El filtro de rango de fechas en viajes ahora usa la fecha de llegada programada a origen (scheduledOriginArrivalAt) en lugar de la fecha de salida, con fallback a la salida cuando no hay fecha programada.",
          "El filtro soporta dos formatos: rango de días completos (YYYY-MM-DD) y rango con precisión horaria (YYYY-MM-DDTHH:MM).",
          "La tabla de viajes ahora ordena por defecto por Llegada programada a origen (descendente). Se agregó ordenamiento por # Viaje (internalId) y opción de invertir dirección.",
          "La columna 'Ll. proy. origen' reemplaza a 'Salida / Llegada' para clarificar el campo por el que se filtra y ordena. La columna 'Salida / Llegada' se mantiene con la información real.",
          "Todo el filtrado y ordenamiento se procesa en el servidor para máxima eficiencia, incluso con miles de viajes.",
        ],
      },
    ],
  },
  {
    version: "v0.77.0",
    date: "2026-09-15",
    sections: [
      {
        title: "Exportación de valorización — nueva columna PROV SUC",
        type: "feat" as const,
        items: [
          "Las exportaciones de valorización (por remito y por viaje) incluyen ahora la columna PROV SUC con el valor \"CC\".",
        ],
      },
    ],
  },
  {
    version: "v0.76.0",
    date: "2026-09-15",
    sections: [
      {
        title: "Exportación de valorización — nueva columna PROV.",
        type: "feat" as const,
        items: [
          "Las exportaciones de valorización (viajes y remitos) incluyen ahora la columna PROV. con el número de PV Cuenta del chofer asignado al viaje.",
          "Si el chofer no tiene PV Cuenta configurado, la celda se marca en rojo en la previsualización y la fila queda excluida del CSV descargado.",
        ],
      },
      {
        title: "Fechas locales en Dashboard y KPI",
        type: "fix" as const,
        items: [
          "Los rangos de fechas del Dashboard (hoy, esta semana, este mes) y de la página KPI ahora se calculan en hora local (ART) en lugar de UTC, evitando que los datos del día aparezcan cortados cerca de la medianoche.",
        ],
      },
      {
        title: "Clientes — formulario de alta corregido",
        type: "fix" as const,
        items: [
          "Los selectores de País y Provincia/Estado en el formulario de nuevo cliente ahora muestran el nombre legible en lugar del identificador interno.",
        ],
      },
      {
        title: "Fix — error al cambiar estado de viaje a Completado",
        type: "fix" as const,
        items: [
          "Al abrir el formulario de edición de un viaje cuyo campo 'Anticipo de viaje' tenía un valor anterior a la migración (booleano false), el select mostraba 'false' y el servidor rechazaba el guardado. Ahora ese valor se normaliza automáticamente a 'Sin anticipo' al abrir el formulario.",
          "Los mensajes de error del servidor ahora son más descriptivos: si el servidor rechaza un campo inválido, el mensaje muestra exactamente qué campo falló y por qué, en lugar de un mensaje genérico.",
        ],
      },
    ],
  },
  {
    version: "v0.75.0",
    date: "2026-09-14",
    sections: [
      {
        title: "Dashboard — KPI operativos y estado de flota por línea de trabajo",
        type: "feat" as const,
        items: [
          "Nueva página KPI (/system/kpi) con dos pestañas: Flota (estado operativo en tiempo real) y Acumulado (métricas por rango de fechas).",
          "La pestaña Flota muestra por línea de trabajo (Batea, Nacional, Internacional) cuántos móviles están en viaje, con carga, libres, en reparación o con chofer fuera de servicio.",
          "La pestaña Acumulado permite filtrar por fechas y línea de trabajo: total de viajes, KM recorridos, ranking de choferes por KM y ranking de clientes por viajes.",
          "Nueva sección Operaciones en el Dashboard principal con tabla de cargas y viajes por línea de trabajo y gráfico de eficiencia (viajes con carga vs. sin carga). Período configurable: hoy, esta semana, este mes.",
          "El estado de flota en el Dashboard ahora incluye métricas adicionales: móviles en viaje activo, móviles con carga, móviles libres, en reparación y con chofer inactivo.",
        ],
      },
      {
        title: "Cargas Internacionales — kanban mejorado con filtros de ubicación y tarjeta detallada",
        type: "feat" as const,
        items: [
          "El kanban de Cargas Internacionales ahora usa tarjetas con información completa: cliente, chofer, móvil, origen → destino con bandera de país y fecha programada de llegada a origen.",
          "Nuevo filtro de ubicación para origen y destino: permite seleccionar uno o varios países y/o ciudades para mostrar solo las cargas relevantes.",
          "Filtro por rango de fechas integrado directamente en la página.",
          "Botón de compartir por tarjeta: copia al portapapeles un resumen del envío con todos los hitos completados (✅) y pendientes (⏳), listo para enviar por WhatsApp.",
          "Los hitos del kanban se redujeron de 13 a 8 pasos visibles. Los pasos intermedios de aduana y frontera se registran de forma implícita al avanzar al siguiente hito (sin clics extra).",
          "Las columnas vacías ahora se muestran compactas para aprovechar el espacio horizontal. Solo las columnas con cargas muestran el área de drop completa.",
        ],
      },
      {
        title: "Viajes — filtro por rango de fechas",
        type: "feat" as const,
        items: [
          "Nueva opción de filtrado en la tabla de Viajes: desde / hasta por fecha de salida. El filtro usa la zona horaria configurada en el servidor (igual que el filtro de Cargas).",
          "El filtro de fechas se procesa en el servidor, por lo que funciona correctamente sin importar cuántos viajes haya en la base de datos.",
        ],
      },
      {
        title: "Empresas — ciudad filtrada por país",
        type: "feat" as const,
        items: [
          "En el formulario de Empresas, al seleccionar un país el selector de ciudad solo muestra las ciudades de ese país. Al seleccionar una ciudad sin país previo, el país se completa automáticamente.",
        ],
      },
      {
        title: "Cargas — campo N° Externo",
        type: "feat" as const,
        items: [
          "Nuevo campo opcional N° Externo en el formulario de cargas para registrar el identificador del cliente (remito, orden de compra, etc.).",
        ],
      },
    ],
  },
  {
    version: "v0.74.0",
    date: "2026-09-15",
    sections: [
      {
        title: "Cargas — exportaciones mejoradas",
        type: "feat" as const,
        items: [
          "Preview de exportación de Datos de viaje y Valorización: las filas con datos incompletos ahora se muestran en amarillo con la celda problemática en rojo, en lugar de ocultarse silenciosamente.",
          "Valorización: las cargas sin viaje asignado o sin ruta registrada ya aparecen en el preview para que el usuario sepa qué quedó excluido de la descarga.",
          "Valorización: las cargas de tipo Internacional o Nacional seleccionadas junto a bateas ahora aparecen en el preview marcadas como 'No es batea', en lugar de generar un resultado vacío sin explicación.",
          "Datos de viaje (cargas): la columna FECHA ahora usa la Llegada proyectada a origen del envío en lugar de la fecha de salida del viaje.",
          "Los archivos CSV y XLS descargados solo incluyen filas completas; las filas con problemas quedan visibles en el preview pero se excluyen de la descarga.",
        ],
      },
    ],
  },
  {
    version: "v0.73.0",
    date: "2026-09-14",
    sections: [
      {
        title: "Fix — filtros de fecha corregidos por zona horaria",
        type: "fix" as const,
        items: [
          "Los filtros por fecha en Cargas, Contratos y SmartGT ahora muestran correctamente los registros del día seleccionado en hora argentina. Antes, una carga del 11/09 a las 23:00 hora local aparecía como si fuera del 12/09 porque el servidor comparaba en UTC.",
          "La zona horaria del sistema se configura en app.config.json y se aplica automáticamente a todos los filtros de fecha del servidor.",
        ],
      },
    ],
  },
  {
    version: "v0.70.0",
    date: "2026-09-11",
    sections: [
      {
        title: "Unidades — búsqueda y selects corregidos",
        type: "fix" as const,
        items: [
          "La búsqueda en la tabla de Unidades ahora filtra en todas las páginas: antes solo buscaba dentro de los primeros 25 resultados cargados y la unidad podía no encontrarse si estaba en una página posterior.",
          "El filtro de búsqueda se envía al servidor (PostgreSQL ILIKE) y el resultado ya llega filtrado, sin importar cuántas unidades haya en total.",
          "Los selects de Semi-trailer y Camión en Móviles, Viajes y Detalle de carga ahora buscan por patente en el servidor al escribir: ya no se cargan las primeras 25 unidades fijas sino que se consulta con el texto ingresado con 300 ms de debounce.",
        ],
      },
    ],
  },
  {
    version: "v0.69.0",
    date: "2026-09-10",
    sections: [
      {
        title: "Cargas y Viajes — cambio masivo de estado disponible para operadores",
        type: "fix" as const,
        items: [
          "Corregido: los operadores recibían \"Forbidden\" al intentar cambiar el estado o la facturación de múltiples cargas o viajes desde la barra de selección.",
          "El endpoint de cambio masivo (bulk-status) estaba restringido solo a administradores, ignorando que el rol operador tiene permisos completos sobre cargas y viajes.",
          "Los mensajes de error ahora aparecen en español: \"No tenés permisos para realizar esta acción\" en lugar de \"Forbidden\".",
        ],
      },
    ],
  },
  {
    version: "v0.68.0",
    date: "2026-09-10",
    sections: [
      {
        title: "Kanban — alertas de demora en origen y destino",
        type: "feat" as const,
        items: [
          "Nueva Regla B en el Kanban: detecta camiones parados demasiado tiempo en el punto de carga (estado Activo) o en el punto de descarga (estado En curso) aunque ya hayan registrado llegada.",
          "Para Activo: si el último evento de origen (llegada o inicio de carga) supera 2 horas, la tarjeta se vuelve amarilla. Pasadas las 5 horas se vuelve roja con el tiempo exacto transcurrido (ej. \"3h 20m parado en origen\").",
          "Para En curso: misma lógica cuando el camión llegó a destino pero todavía no se finalizó el envío. Usa el evento más reciente entre llegada a destino y descarga registrada.",
          "Los umbrales son configurables sin reiniciar la API: editar originStaleYellowHours / originStaleRedHours / destinationStaleYellowHours / destinationStaleRedHours en apps/api/app.config.json.",
          "Nuevo filtro \"Solo alertas\" en el Kanban (pill rojo en la barra de filtros): oculta todas las tarjetas sin alerta activa y facilita trabajar el backlog urgente.",
          "El badge de alerta por tarjeta ahora muestra un texto descriptivo con tiempo transcurrido o ventana restante, distinto según la regla que lo activó.",
        ],
      },
      {
        title: "Sentry — monitoreo de errores en el panel web",
        type: "feat" as const,
        items: [
          "Se integró Sentry en el panel web: los errores de JavaScript no capturados se reportan automáticamente con el contexto del usuario (nombre y email).",
          "El usuario queda identificado en Sentry al hacer login y se limpia al cerrar sesión o al confirmar la expiración de sesión.",
          "Toda la app queda envuelta en un ErrorBoundary de Sentry: si un componente falla de forma irrecuperable se muestra un mensaje claro en lugar de pantalla en blanco.",
          "Los source maps se suben a Sentry automáticamente en cada build de producción (cuando las variables SENTRY_AUTH_TOKEN y VITE_SENTRY_DSN están configuradas). En desarrollo y CI el plugin es un no-op.",
        ],
      },
    ],
  },
  {
    version: "v0.67.0",
    date: "2026-09-10",
    sections: [
      {
        title: "Viajes — Volver a base",
        type: "feat" as const,
        items: [
          "Nuevo botón \"Volver a base\" en la barra de selección de Viajes: se habilita al seleccionar exactamente 1 viaje y crea un viaje de regreso desde el destino del chofer hasta Chivilcoy (ciudad base configurable).",
          "También disponible en el menú de 3 puntos (⋮) de cada fila sin necesidad de usar los checkboxes.",
          "Fecha de salida calculada automáticamente: usa la fecha de llegada del viaje origen si existe, o la fecha de salida + 8 h. Fecha de llegada estimada en base a KM de ruta ÷ 80 km/h.",
          "Preview antes de confirmar: muestra origen, destino, chofer, camión, semi, fechas calculadas y KM de ruta. Los bloqueos (sin destino, sin fechas, destino ya es la base) se muestran dentro del popup como banner rojo.",
          "La ciudad base se configura en apps/api/app.config.json mediante el ID de la parte (basePartyId). La velocidad promedio (averageSpeedKmh) también es configurable.",
        ],
      },
      {
        title: "Viajes — Exportación mejorada",
        type: "feat" as const,
        items: [
          "Los botones \"Datos de viaje\" y \"Valorización\" en la barra de selección ahora funcionan para todos los viajes, incluyendo los de interconexión y regreso a base que no tienen carga asociada.",
          "El preview de exportación marca en amarillo las filas con problemas y en rojo las celdas específicas que faltan (sin destino, sin fecha, sin PROV NRO, sin ruta). Esas filas se excluyen automáticamente del CSV y XLS descargado.",
          "El contador del preview muestra cuántas filas válidas se exportarán y cuántas fueron excluidas por problemas.",
        ],
      },
      {
        title: "Partes — copiar ID",
        type: "feat" as const,
        items: [
          "Se agregó un botón de copiar UUID junto al nombre de cada parte en la tabla, igual al que ya existía en Clientes. Útil para configurar app.config.json con el ID de la parte base.",
        ],
      },
    ],
  },
  {
    version: "v0.66.0",
    date: "2026-09-10",
    sections: [
      {
        title: "Viajes — interconexión y filtros",
        type: "feat" as const,
        items: [
          "Nuevo botón \"Interconexión\" en la barra de selección de Viajes: se habilita al seleccionar exactamente 2 viajes del mismo chofer, y crea un viaje de conexión entre el destino del primero y el origen del segundo, con estado Completado y sin carga asociada.",
          "El orden de los viajes se determina por fecha de salida (el más temprano es el primero); los recursos del viaje de interconexión (chofer, camión, semi) se toman del segundo viaje.",
          "La fecha del viaje de interconexión es el punto medio entre las fechas de salida de ambos viajes seleccionados.",
          "Preview antes de confirmar: muestra origen, destino, chofer, camión, semi, fecha calculada y KM de ruta (si existe). Si la ruta no está configurada, se advierte que el campo KM quedará vacío.",
          "Si los viajes no cumplen las condiciones para crear la interconexión (falta fecha de salida en alguno, o el destino del primero coincide con el origen del segundo), el botón sigue siendo clickeable y el motivo exacto se muestra dentro del popup.",
          "Filtros de la pantalla de Viajes (chofer, facturación y búsqueda de texto) ahora se ejecutan en el servidor — antes solo filtraban la página visible y se perdían resultados de otras páginas.",
        ],
      },
    ],
  },
  {
    version: "v0.65.0",
    date: "2026-09-09",
    sections: [
      {
        title: "Performance — consultas del servidor hasta 10× más rápidas",
        type: "feat" as const,
        items: [
          "La pantalla de viajes del chofer (app mobile) carga hasta 10 veces más rápido: se eliminó una consulta doble a la base de datos y ahora se resuelve en una sola operación.",
          "Los stats mensuales de Contratos dejaron de hacer una consulta por semana (4–5 viajes al servidor) y ahora se calculan en una sola consulta agrupada.",
          "Se agregaron índices optimizados para las búsquedas de rutas por origen/destino y para la resolución de choferes por usuario — ambas operaciones eran full scan de tabla.",
        ],
      },
    ],
  },
  {
    version: "v0.64.0",
    date: "2026-09-09",
    sections: [
      {
        title: "Performance — mejoras de velocidad en el servidor",
        type: "feat" as const,
        items: [
          "Se agregaron índices en las tablas principales (envíos, viajes, documentos, valorizaciones) para acelerar las consultas más frecuentes. Las pantallas de Envíos y Viajes deberían cargar notablemente más rápido.",
          "La edición de envíos (PATCH) es más eficiente: los productos ahora se insertan en una sola operación en lugar de una por una, y las queries internas se ejecutan en paralelo cuando es posible.",
        ],
      },
    ],
  },
  {
    version: "v0.63.0",
    date: "2026-09-09",
    sections: [
      {
        title: "Clientes — copiar ID con un click",
        type: "feat" as const,
        items: [
          "En la tabla de Clientes, al lado del código de cada cliente aparece un ícono de copiar. Hacé click para copiar el UUID del cliente al portapapeles — útil para consultas y filtros avanzados.",
        ],
      },
      {
        title: "Contratos — conteo de cargas alineado con la página Cargas",
        type: "fix" as const,
        items: [
          "El conteo de cargas por semana en Contratos ahora usa la fecha programada de llegada al origen (igual que el filtro de fecha en la página Cargas), en lugar de la fecha real de carga registrada por el chofer. Esto hace que los números sean comparables entre ambas vistas.",
        ],
      },
    ],
  },
  {
    version: "v0.62.0",
    date: "2026-09-09",
    sections: [
      {
        title: "Cargas — ordenar por toneladas de descarga",
        type: "feat" as const,
        items: [
          "La columna Tons. en la tabla de Cargas ahora es sorteable: hacé clic en el encabezado para ordenar de mayor a menor o de menor a mayor según el peso de descarga registrado. Los registros sin descarga aparecen al final.",
        ],
      },
    ],
  },
  {
    version: "v0.60.0",
    date: "2026-09-09",
    sections: [
      {
        title: "Vista previa del sitio en WhatsApp y Telegram",
        type: "feat" as const,
        items: [
          "Al compartir el link de CDC en WhatsApp o Telegram ahora se muestra una vista previa con el logo oficial de CDC en lugar de una imagen genérica de delivery.",
        ],
      },
    ],
  },
  {
    version: "v0.59.0",
    date: "2026-09-09",
    sections: [
      {
        title: "Informes — mejoras en búsqueda y verificaciones técnicas",
        type: "feat" as const,
        items: [
          "El buscador de Informes ahora indica explícitamente que se puede buscar por número de carga (#ID), además de remitente, destino y chofer.",
          "El badge de verificaciones técnicas se muestra en rojo cuando algún ítem de camión o acoplado tiene estado 'Observado', sin requerir ningún procesamiento adicional en el servidor.",
          "Las cargas con verificaciones técnicas pero sin plan de viaje ahora aparecen correctamente en el listado de Informes.",
        ],
      },
    ],
  },
  {
    version: "v0.58.0",
    date: "2026-09-09",
    sections: [
      {
        title: "Fix — paginación de Cargas mostraba total incorrecto",
        type: "fix" as const,
        items: [
          "El pie de página de la tabla de Cargas mostraba el total de la página actual (ej: '1–100 de 100 resultados') en lugar del total real de registros. Ahora refleja correctamente el conteo del servidor (ej: '1–100 de 132 resultados').",
        ],
      },
    ],
  },
  {
    version: "v0.57.0",
    date: "2026-09-09",
    sections: [
      {
        title: "Cargas — filtros del lado del servidor",
        type: "feat" as const,
        items: [
          "Todos los filtros de la pantalla de Cargas (búsqueda de texto, estados, línea de trabajo, cliente, remito, facturación, chofer, operadores, rango de fechas y orden) ahora se procesan en la base de datos. Antes se aplicaban sobre la página ya cargada, ignorando registros en otras páginas.",
          "El filtro por rango de fechas ahora funciona correctamente sobre todos los registros, no solo los de la página visible.",
        ],
      },
      {
        title: "Cargas — logo del cliente en la tabla",
        type: "feat" as const,
        items: [
          "La columna Cli. de la tabla de cargas muestra ahora la imagen del cliente cuando está disponible, con las iniciales en color como fallback. Al actualizar la imagen desde la pantalla de Clientes, la tabla y el kanban se refrescan automáticamente sin esperar la expiración del caché.",
        ],
      },
    ],
  },
  {
    version: "v0.56.0",
    date: "2026-09-08",
    sections: [
      {
        title: "Rendimiento — paginación del lado del servidor",
        type: "feat" as const,
        items: [
          "Las pantallas de Clientes, Cargas y Viajes ahora cargan solo la página actual desde el servidor (25 registros por defecto), eliminando timeouts que ocurrían al traer miles de registros de una sola vez.",
          "La búsqueda de clientes se procesa en la base de datos con ILIKE, incluyendo búsqueda dentro de los códigos SGT.",
          "Los selects de cliente en Cargas, Detalle de carga, Contratos y SmartGT traen hasta 500 registros en una sola llamada optimizada.",
        ],
      },
      {
        title: "Rendimiento — reducción de polling",
        type: "feat" as const,
        items: [
          "La pantalla de Cargas pasó de refrescar datos cada 10 segundos a cada 30 segundos, reduciendo significativamente la carga sobre la base de datos.",
          "Se agregó refresco automático al volver a la pestaña del navegador (refetchOnWindowFocus).",
        ],
      },
      {
        title: "Fix — Tipos de servicio no cargaban",
        type: "fix" as const,
        items: [
          "La pantalla de Tipos de servicio devolvía error de validación UUID porque la ruta era capturada por el handler de detalle de viaje. El orden de registro de rutas fue corregido.",
        ],
      },
    ],
  },
  {
    version: "v0.55.0",
    date: "2026-09-08",
    sections: [
      {
        title: "Kanban de Cargas — rediseño de cards",
        type: "feat" as const,
        items: [
          "Las cards del Kanban muestran ahora el logo del cliente (con fallback de iniciales en color), número de móvil, nombre completo del chofer, origen y destino (nombre del remitente/destinatario en negrita), fecha programada y km.",
          "El footer con las acciones (Asignar, Confirmar, Comenzar, Finalizar, WhatsApp, Ver, Editar, Eliminar) se oculta por defecto y se revela al hacer hover sobre la card, manteniendo la vista compacta.",
          "Los logos de cliente se cargan desde un endpoint dedicado con caché de 50 minutos, evitando el parpadeo que ocurría al refrescar la lista cada 5 segundos.",
          "Origen y destino muestran el nombre del remitente/destinatario en lugar de la ciudad, con la ciudad como fallback.",
        ],
      },
      {
        title: "Kanban de Cargas — alertas visuales por estado",
        type: "feat" as const,
        items: [
          "Las cards del estado Planificado cambian su fondo a amarillo tras 15 minutos sin confirmar, naranja tras 30 minutos y rojo tras 60 minutos. El reloj usa el campo plannedAt (inmutable) para que editar otros datos del registro no reinicie la cuenta.",
          "Las cards del estado Activo muestran alerta amarilla cuando faltan menos de 2 horas para la llegada proyectada a origen, naranja al superar la hora proyectada y rojo al superar 2 horas de retraso sin registrar la llegada real.",
          "Las cards del estado En curso siguen la misma lógica que Activo pero comparando contra la llegada proyectada a destino.",
          "El encabezado de cada columna muestra un badge con la cantidad de cards en alerta.",
<span>La lógica de alertas se centralizó en <code className="text-xs bg-muted px-1 rounded font-mono">shipmentAlertLevel()</code> dentro de <code className="text-xs bg-muted px-1 rounded font-mono">packages/shared</code> — reutilizable en cualquier vista o la app mobile. <Link to="/docs/alertas-envios" className="text-primary underline-offset-2 hover:underline whitespace-nowrap">Ver documentación →</Link></span>
        ],
      },
      {
        title: "Filtros de Cargas — persistencia en el browser",
        type: "feat" as const,
        items: [
          "Los filtros de fecha desde/hasta, estado de facturación y tamaño de página ahora se guardan en localStorage y se restauran al volver a la pantalla, igual que los demás filtros.",
        ],
      },
      {
        title: "Cargas — barra de selección de registros mejorada",
        type: "feat" as const,
        items: [
          "La barra de acciones masivas se rediseñó como una línea horizontal compacta con badge de conteo, acciones de estado/facturación y botones de exportación inline.",
          "Cualquier carga puede seleccionarse con el checkbox, incluso las que no tienen km de ruta registrado (la restricción anterior sólo aplicaba a la exportación).",
        ],
      },
    ],
  },
  {
    version: "v0.54.0",
    date: "2026-09-07",
    sections: [
      {
        title: "Cargas — Barra de exportación y acciones masivas mejoradas",
        type: "feat" as const,
        items: [
          "Los 4 tipos de exportación (Datos de carga, Documentos de carga, Valorización, Datos de viaje) ahora se muestran como botones individuales en la barra de selección, reemplazando el menú desplegable anterior.",
          "La barra de acciones masivas se reorganizó en dos secciones claramente separadas: 'ACCIONES' (cambio de estado operativo y de facturación) y 'EXPORTAR' (los 4 botones de exportación).",
          "Cambiar el estado de facturación de una carga ahora actualiza automáticamente el estado de facturación de todos sus viajes asociados en una sola operación. El diálogo de confirmación advierte explícitamente sobre este efecto en cascada.",
          "Luego de aplicar un cambio masivo de estado o facturación, las filas seleccionadas permanecen marcadas para facilitar operaciones encadenadas.",
          "El panel de viajes dentro del detalle de una carga ahora muestra el estado operativo y el estado de facturación apilados en la columna 'Estado', igual que en la tabla principal de cargas.",
        ],
      },
    ],
  },
  {
    version: "v0.53.0",
    date: "2026-09-07",
    sections: [
      {
        title: "Viajes — Recursos históricos del trip",
        type: "fix" as const,
        items: [
          "El formulario de edición de un viaje ya no muestra el selector de móvil: en su lugar se ven los recursos asignados al momento de creación (chofer, camión y semi) como snapshot histórico.",
          "Un botón 'Cambiar recursos' permite reemplazar chofer, camión y semi de forma explícita e independiente, sin afectar el móvil ni otros viajes.",
          "Se corrige un bug por el cual guardar un viaje sobreescribía el chofer y semi con los valores actuales del móvil en lugar de respetar el snapshot original.",
          "La tabla de viajes dentro del detalle de una carga adopta el mismo formato visual que la tabla global: salida y llegada en una sola columna apilada, y acciones agrupadas en un menú contextual.",
        ],
      },
    ],
  },
  {
    version: "v0.52.0",
    date: "2026-09-05",
    sections: [
      {
        title: "Auditoría de viajes",
        type: "feat" as const,
        items: [
          "Todas las operaciones sobre viajes (crear, editar, eliminar, restaurar, rechazar, dividir y cambio masivo de estado) quedan registradas en el historial de auditoría.",
          "Cada registro incluye el snapshot anterior y posterior, el campo modificado y el usuario responsable.",
        ],
      },
    ],
  },
  {
    version: "v0.51.0",
    date: "2026-09-05",
    sections: [
      {
        title: "Estado de facturación en cargas y viajes",
        type: "feat" as const,
        items: [
          "Nuevo campo 'Facturación' en cargas y viajes con los estados: Pendiente, Liquidado, Facturado y Cerrado.",
          "El estado de facturación se puede cambiar directamente desde la tabla de cargas haciendo clic en el badge, sin necesidad de abrir el formulario de edición.",
          "Nueva columna y filtro de facturación en la tabla de viajes.",
        ],
      },
      {
        title: "Edición masiva de cargas y viajes",
        type: "feat" as const,
        items: [
          "Nueva barra de acciones masivas en la tabla de viajes: permite cambiar el estado operativo, el estado de facturación y el anticipo en múltiples viajes seleccionados a la vez.",
          "Nueva acción masiva en la tabla de cargas para cambiar el estado operativo y el estado de facturación en varios registros simultáneamente.",
          "Todas las acciones masivas muestran un diálogo de confirmación antes de aplicar los cambios.",
        ],
      },
      {
        title: "Tipo de documento en remitos",
        type: "feat" as const,
        items: [
          "Los documentos de viaje (remitos) ahora se pueden clasificar como 'Remito de carga' o 'Remito de descarga' al momento de subirlos o editarlos.",
          "El tipo de documento se muestra como badge en la tabla de documentos del detalle de carga.",
        ],
      },
      {
        title: "Anticipo de viaje — gestión por viaje",
        type: "feat" as const,
        items: [
          "El campo 'Anticipo de viaje' se movió del nivel de carga al nivel de viaje. Ahora cada viaje tiene su propio indicador de anticipo.",
          "Se puede activar o desactivar el anticipo desde la tabla de viajes dentro del detalle de una carga, sin necesidad de abrir el formulario completo.",
          "Nueva acción masiva para marcar o desmarcar el anticipo en múltiples viajes a la vez desde la tabla de viajes.",
        ],
      },
      {
        title: "Avatar de clientes",
        type: "feat" as const,
        items: [
          "Los clientes ahora pueden tener un logo o avatar. Se sube directamente desde el formulario de edición y se almacena de forma privada en S3.",
          "El avatar se muestra en la tabla de clientes y en la tabla de cargas junto al nombre del cliente.",
        ],
      },
      {
        title: "Configuración CDC En Ruta por cliente",
        type: "feat" as const,
        items: [
          "Nueva sección de configuración en el detalle de cada cliente para la app del chofer: habilitar o deshabilitar el remito de carga obligatorio y la planificación de ruta.",
        ],
      },
      {
        title: "Detalle de cliente — edición inline",
        type: "feat" as const,
        items: [
          "El formulario de edición de clientes ahora funciona de forma inline dentro de la página de detalle, sin abrir un panel lateral.",
          "Se puede editar nombre, código, documentación, dirección y configuración CDC En Ruta directamente en la pantalla de detalle.",
        ],
      },
      {
        title: "Fix: semanas de contratos y regla ISO 8601",
        type: "fix" as const,
        items: [
          "Corrección en la asignación de semanas al mes: una semana ahora pertenece al mes donde cae su jueves (regla ISO 8601). Antes, semanas que comenzaban en un mes y terminaban en otro se asignaban de forma incorrecta.",
          "La generación de semanas y el cálculo de KPIs mensuales usan la misma regla, garantizando consistencia en los reportes de contratos.",
        ],
      },
      {
        title: "Limpieza de columnas obsoletas",
        type: "fix" as const,
        items: [
          "Se eliminaron de la base de datos las columnas 'hasFuelAdvance', 'routingComplete', 'deliveryNoteValidated' e 'isInvoiced' de la tabla de cargas, que ya no se usaban.",
          "Se eliminaron las columnas 'fileUrl' de documentos de viaje, valorizaciones y documentos de chofer — el acceso a archivos siempre usa URLs firmadas (presign).",
        ],
      },
    ],
  },
  {
    version: "v0.50.0",
    date: "2026-09-01",
    sections: [
      {
        title: "Destinos finales — gestión desde la app",
        type: "feat" as const,
        items: [
          "Los destinos finales de batea (antes hardcodeados como slugs) ahora se gestionan como una entidad propia en la base de datos, accesible desde Batea → Destinos finales.",
          "CRUD completo con búsqueda, paginación, soft delete y restauración. Permisos: admin y operator.",
          "Los selects de 'Destino final' en Cargas y en el detalle de una carga ahora cargan la lista desde la API en lugar de una constante fija.",
          "La columna en la tabla de cargas y en la app del chofer sigue mostrando el nombre del destino.",
        ],
      },
      {
        title: "Cargas — Cancelación con motivo",
        type: "feat" as const,
        items: [
          "Nueva acción 'Cancelar' en el menú de cada carga (ícono ··· en la tabla). Requiere seleccionar un motivo (Clima, Rotura, Chofer, Externo, Otro) y una descripción.",
          "Al cancelar, todos los viajes activos de la carga (pendiente o en curso) se cancelan en una única transacción.",
          "El detalle de la carga muestra un banner rojo con el motivo y la descripción cuando el estado es 'Cancelado'.",
          "El estado 'Cancelado' aparece en los filtros de la tabla pero no en el selector de estado del formulario de edición — solo se puede cancelar desde la acción dedicada.",
          "Si un admin revierte manualmente el estado desde 'Cancelado', el motivo y la descripción se limpian automáticamente.",
          "La app del chofer excluye cargas canceladas de la pestaña 'Activos'.",
        ],
      },
      {
        title: "Contratos — Override de objetivo mensual",
        type: "feat" as const,
        items: [
          "Nuevo botón 'Objetivo mensual' en la pantalla de detalle de un contrato. Permite definir un objetivo específico para el mes en curso, independiente del valor del contrato.",
          "Si hay un override, el cálculo de KPIs (% cumplimiento, camiones necesarios) y la generación de semanas usan ese valor. Si no, se aplica el objetivo del contrato como fallback.",
          "El encabezado del mes muestra un badge 'objetivo personalizado' cuando hay override activo.",
          "Los envíos cancelados se excluyen del cálculo de tonelaje real en contratos.",
        ],
      },
      {
        title: "Contratos — Refactor de novedades de semana",
        type: "feat" as const,
        items: [
          "Se eliminó el sub-módulo de 'Novedades' por semana (eventos de clima, rotura, etc. con ajuste de TN). El modelo de ajuste por evento se reemplaza por el override de objetivo mensual, que es más directo y preciso.",
          "La generación de semanas ahora recorta la última semana del mes al último día del mes en lugar de extenderla al domingo siguiente.",
        ],
      },
      {
        title: "Tabla de acciones — menú contextual",
        type: "feat" as const,
        items: [
          "Los botones de acción en la tabla de cargas se unificaron en un menú desplegable (ícono ···). Incluye: Duplicar, Editar, Cancelar (separado) y Eliminar.",
          "El estado 'Cancelado' se muestra en rojo tanto en la tabla de cargas como en el kanban.",
        ],
      },
    ],
  },
  {
    version: "v0.49.0",
    date: "2026-08-28",
    sections: [
      {
        title: "SmartGT — Sub-caso A' (clientes hermanos)",
        type: "feat" as const,
        items: [
          "Los CRTs de clientes del mismo grupo empresarial (ej. Lesaffre do Brasil + SAF do Brasil) transportados por el mismo chofer en fechas próximas se consolidan en un único viaje de exportación.",
          "Exportación de datos de viaje: genera 1 sola fila usando el CRT con el source_id más alto como representante.",
          "Exportación de datos de carga: cada CRT mantiene su propia fila, pero todas referencian el mismo ID VIAJE del representante.",
          "La configuración de grupos hermanos se gestiona en un archivo dedicado (sibling-clients.ts) sin necesidad de migración de base de datos.",
        ],
      },
      {
        title: "App chofer — Limpieza de dependencias",
        type: "fix" as const,
        items: [
          "Eliminada la dependencia expo-dev-client que no es necesaria en producción y generaba conflictos de resolución de módulos en el entorno Bun.",
        ],
      },
    ],
  },
  {
    version: "v0.48.0",
    date: "2026-08-27",
    sections: [
      {
        title: "Viajes — ID autoincremental",
        type: "feat" as const,
        items: [
          "Cada viaje tiene ahora un ID propio autoincremental (internalId) independiente del envío al que pertenece.",
          "La tabla de Viajes muestra una nueva columna '# Viaje' con el identificador en azul (ej: #30), separada de la columna 'Carga' que sigue mostrando el ID del envío.",
          "La tabla de viajes dentro del detalle de una carga también muestra la columna '# Viaje'.",
          "Los exportadores CSV/XLS usan el nuevo ID: datos de carga (col 18 ID VIAJE = T30), datos de viaje (col 1 ID EXTERNO = T30).",
        ],
      },
      {
        title: "Viajes — Ordenamiento por columna",
        type: "feat" as const,
        items: [
          "Las columnas '# Viaje' y 'Salida' de la tabla de viajes son ahora clicables para ordenar ascendente o descendente.",
          "El orden por defecto es '# Viaje' descendente (más reciente primero).",
          "Íconos de ordenamiento (↑ ↓ ⇅) en los encabezados para indicar el estado activo.",
        ],
      },
      {
        title: "Cargas — Exportación de datos de viaje",
        type: "feat" as const,
        items: [
          "Nuevo botón 'Exportar datos de viaje' en la barra de selección de la tabla de cargas, junto a los exportadores existentes.",
          "Genera el mismo CSV de datos de viaje que la página de Viajes, con una fila por cada tramo.",
        ],
      },
      {
        title: "Cargas — Exportadores actualizados",
        type: "feat" as const,
        items: [
          "Datos de carga: col 18 ID VIAJE ahora es el ID del último viaje del envío (ej: T30) en lugar de repetir el ID del envío.",
          "Documentos de carga: TIPO cambia de CR a RE, LETRA cambia de T a X, DESCRIPCION muestra 'Origen - Destino - Cliente'.",
          "Valorización: DESCRIP muestra 'Origen - Destino - Cliente' en lugar del formato de KM anterior.",
          "Los cuatro exportadores (datos, documentos, valorización y viaje) ahora incluyen cargas en cualquier estado, no solo 'Finalizado'.",
        ],
      },
    ],
  },
  {
    version: "v0.47.0",
    date: "2026-08-22",
    sections: [
      {
        title: "Cargas — Nuevo estado 'Confirmado'",
        type: "feat" as const,
        items: [
          "Se agrega el estado 'Confirmado' (color violeta) al ciclo de vida de los envíos, disponible en la lista de cargas y en la vista kanban.",
          "El estado queda disponible como filtro de badge en la tabla y como columna independiente en el kanban.",
        ],
      },
      {
        title: "Cargas — Exportación CSV / XLS con selección de filas",
        type: "feat" as const,
        items: [
          "Nueva columna de checkboxes en la tabla de cargas (modo lista): permite seleccionar uno o varios envíos para exportar.",
          "Las cargas sin KM de ruta asignado quedan deshabilitadas para selección (ícono de advertencia en la columna N°).",
          "Barra de acción flotante al seleccionar filas: botones 'Exportar datos de carga', 'Exportar documentos de carga' y 'Exportar valorización'.",
          "Vista previa del CSV antes de descargar: tabla scrolleable con encabezados fijos, conteo de filas y botones de descarga en formato CSV y XLS (97–2003, sin dependencias externas).",
          "Solo se exportan cargas batea con estado 'Finalizado'; las demás se omiten silenciosamente.",
          "Selector de tamaño de página 25 / 50 / 100 en la barra de herramientas.",
        ],
      },
      {
        title: "Cargas — Filtro por fecha de salida origen",
        type: "feat" as const,
        items: [
          "Nuevo filtro de rango de fechas (desde / hasta) para la columna 'Salida' en la tabla de cargas.",
          "Filtra por la fecha programada de salida en origen (campo scheduledOriginArrivalAt). Botón × para limpiar el rango.",
          "La columna 'Salida' ahora muestra fecha y hora apiladas en dos líneas para mayor compacidad.",
          "La celda de 'Cliente' incluye tooltip al truncar nombres largos.",
        ],
      },
      {
        title: "Viajes — Exportación CSV / XLS con selección de filas",
        type: "feat" as const,
        items: [
          "Nueva columna de checkboxes en la tabla de viajes: permite seleccionar viajes para exportar sus datos.",
          "Barra de acción flotante al seleccionar: botón 'Exportar datos de viaje' que genera un CSV con el mismo formato que el exportador de SmartGT.",
          "Vista previa del CSV antes de descargar con tabla scrolleable, conteo de filas y descarga en CSV y XLS.",
          "Selector de tamaño de página 25 / 50 / 100 en la barra de herramientas.",
        ],
      },
      {
        title: "Viajes — Asignación automática de fecha de salida y llegada",
        type: "feat" as const,
        items: [
          "Al cambiar el estado de un viaje de 'Pendiente' a 'En curso' se asigna automáticamente la fecha y hora de salida real, si todavía no tenía una cargada.",
          "Al cambiar el estado de un viaje de 'En curso' a 'Completado' se asigna automáticamente la fecha y hora de llegada real, si todavía no tenía una cargada.",
          "La asignación automática ocurre en el servidor (aplica también a cambios de estado desde la app del chofer) y no sobreescribe una fecha ya cargada manualmente.",
          "Los formularios de edición de viaje en la página de Viajes y en el detalle de Envío autocompletan el campo de fecha correspondiente al cambiar el estado, permitiendo ajustarlo antes de guardar.",
        ],
      },
      {
        title: "Sidebar — Rediseño visual",
        type: "feat" as const,
        items: [
          "Barra indicadora vertical de color de acento en la sección activa del sidebar.",
          "Encabezados de sección en negrita con fondo sutil al estar activos, y transición suave al hover.",
          "Ítems de sub-navegación más compactos (text-xs, pl-6) y menor espaciado entre grupos.",
        ],
      },
      {
        title: "Documentación interna — Guías de exportación",
        type: "feat" as const,
        items: [
          "Nueva guía '/docs/exportacion-cargas': mapeo columna a columna de los 3 exportadores CSV de cargas batea (datos, documentos y valorización) con diferencias respecto a SmartGT.",
          "Nueva guía '/docs/exportacion-viajes': patrón estándar de checkboxes, paginación 25/50/100, barra de selección y preview CSV/XLS — checklist para replicar en nuevas páginas.",
        ],
      },
      {
        title: "API — Monitoreo de errores con Sentry",
        type: "feat" as const,
        items: [
          "Integración de Sentry en la API (@sentry/bun): se activa automáticamente cuando la variable de entorno SENTRY_DSN está configurada; no tiene efecto en desarrollo.",
          "Los métodos logger.warn, logger.error y logger.fatal envían breadcrumbs a Sentry; los errores con campo `err` se capturan como excepciones con contexto completo.",
          "Handler global .onError en Hono captura excepciones no manejadas en rutas y las reporta a Sentry.",
          "Helper captureException() en lib/sentry.ts para uso en bloques catch de rutas individuales.",
        ],
      },
    ],
  },
  {
    version: "v0.46.0",
    date: "2026-08-19",
    sections: [
      {
        title: "App Mobile — Flujo batea: indicador de paso y verificación técnica obligatoria",
        type: "feat" as const,
        items: [
          "Nueva barra de progreso en cada card de viaje batea: muestra el paso actual (1 de 5 a 5 de 5) con el nombre del hito y segmentos visuales negros/grises.",
          "La verificación técnica del día es ahora obligatoria en todos los pasos activos: si el conductor no la completó, todos los botones de avance quedan bloqueados y se muestra un aviso rojo con botón de acceso directo al wizard.",
          "La verificación técnica se requiere diariamente. La fecha del día se compara con las entradas registradas; un día sin verificación bloquea el avance independientemente de cuántos días previos tenga registrados.",
        ],
      },
      {
        title: "App Mobile — Flujo batea: mejoras de UX en cada paso",
        type: "feat" as const,
        items: [
          "Paso 2 (Carga en origen): el botón principal se mueve al final, debajo del botón de planificación. Una vez cargada la planificación, el botón de actualizar desaparece del card y pasa al detalle del viaje.",
          "Paso 3 (Comenzar viaje): aviso amarillo informando que en el siguiente paso se solicitarán N\u00b0 Remito y toneladas de carga. El botón siempre está habilitado en este paso.",
          "Paso 4 (En tránsito): el botón 'Llegada al destino' siempre visible; se grisa si faltan N\u00b0 Remito o toneladas, y los campos correspondientes muestran borde rojo con mensaje de campo requerido. La ventana de llegada estimada (salida, fecha y horario ETA) se mueve al detalle del viaje; el botón conserva solo el countdown.",
          "Paso 5 (Descarga y cierre): se eliminan el picker de destino final y la lista de documentos del card. Campos Ton. brutas y Tara con validación en rojo si están vacíos; auto-guardado con debounce de 800 ms.",
          "El selector de destino final ya no se muestra una vez iniciada la descarga.",
        ],
      },
      {
        title: "App Mobile — Detalle del viaje: planificación, verificación y ETA",
        type: "feat" as const,
        items: [
          "Cuando ya existe un plan de viaje, el detalle muestra dos botones separados: 'Ver plan de viaje' y 'Actualizar planificación' (acceso solo desde el detalle).",
          "Nueva sección de verificaciones técnicas en el detalle del viaje: historial de todas las entradas ordenadas por fecha, con badge 'HOY', resumen de ítems ok/observados por camión y acoplado, y botón de carga/edición de la verificación del día.",
          "Nueva sección de ventana de llegada estimada en el detalle (paso 4): muestra fecha y hora de salida real más la ventana ETA de 44–48 horas.",
          "Botón 'Planificar viaje' siempre visible en el detalle; abre el wizard si no hay plan o el visor si ya existe.",
        ],
      },
      {
        title: "App Mobile — Foto de remito en paso 5",
        type: "feat" as const,
        items: [
          "Nuevo botón 'Cargar foto de remito' en el paso de descarga y cierre: abre la cámara directamente, sube la foto al servidor y la asocia al viaje. El botón 'Finalizar viaje' permanece bloqueado hasta que haya al menos una foto cargada.",
          "El conteo de documentos del paso 5 se actualiza automáticamente al subir una foto y al volver del detalle del viaje (via AppState).",
        ],
      },
      {
        title: "App Mobile — Mejoras de calidad e infraestructura",
        type: "feat" as const,
        items: [
          "Los errores en hitos batea (llegada al origen, carga, inicio, llegada al destino, descarga, finalizar) ahora muestran un alert al conductor en lugar de fallar silenciosamente, con mensaje específico por acción.",
          "Sentry integrado para monitoreo de errores en producción: identifica al conductor al hacer login y reporta excepciones con contexto del hito y el ID del remito.",
          "Refactorización mayor: funciones de formato de fechas y conversión de unidades centralizadas en `lib/format.ts`; hook `useShipmentDocUpload` para uploads reutilizables; componentes `MilestoneButton` y `OutlineButton` extraídos a `components/ui/`; `TechCheckHistoryList` como componente independiente.",
          "Los errores al tocar los campos de carga de datos ya no abren el modal de detalle del viaje.",
        ],
      },
    ],
  },
  {
    version: "v0.45.0",
    date: "2026-08-17",
    sections: [
      {
        title: "Web — Contratos: generación automática de semanas",
        type: "feat" as const,
        items: [
          "Nuevo botón \"Generar semanas\" en el detalle del contrato: crea automáticamente las semanas de lunes a domingo para el mes seleccionado, respetando la fecha de inicio del contrato. No duplica semanas ya existentes.",
        ],
      },
      {
        title: "Web — Envíos: duplicar envío",
        type: "feat" as const,
        items: [
          "Nuevo botón de duplicado en la tabla de envíos: abre un diálogo para elegir cuántas copias crear (1–20). Las copias heredan todos los datos del envío original excepto el estado, que comienza en Planificado.",
          "En el formulario de creación de envíos batea, un campo adicional permite crear N copias en paralelo con un solo click.",
        ],
      },
      {
        title: "App Mobile — Lista de tramos por viaje",
        type: "feat" as const,
        items: [
          "El modal de detalle de viaje muestra la lista completa de tramos del envío: estado de cada tramo, nombre del chofer y patente del camión asignado. Solo se muestra cuando el viaje del conductor está completado o cancelado.",
        ],
      },
      {
        title: "App Mobile — Correcciones generales",
        type: "fix" as const,
        items: [
          "La lista de viajes muestra correctamente la ciudad del remitente y del destinatario (antes usaba la ciudad de origen/destino de la ruta).",
          "Los filtros de viajes pasaron de tres tabs (Activos / Próximos / Anteriores) a dos (Activos / Anteriores): Activos agrupa todos los viajes en curso sin importar el estado del envío; Anteriores muestra los tramos completados o cancelados del conductor.",
          "El perfil del chofer ahora se actualiza correctamente al iniciar la app sin requerir re-login.",
        ],
      },
    ],
  },
  {
    version: "v0.44.0",
    date: "2026-08-17",
    sections: [
      {
        title: "Web — División de viaje (Split) con análisis de impacto",
        type: "feat" as const,
        items: [
          "El detalle de un envío permite dividir un viaje en dos tramos: Tramo 1 conserva el móvil original y Tramo 2 recibe un móvil, chofer, camión y semi independientes.",
          "Al seleccionar los recursos para el Tramo 2, el sistema detecta automáticamente conflictos: si el chofer o el semi elegidos pertenecen al Tramo 1, se muestra un aviso explicando el impacto antes de confirmar.",
          "Si se selecciona un camión que pertenece a otro móvil, el campo de móvil cambia automáticamente para mantener coherencia.",
        ],
      },
      {
        title: "Web — Revertir finalización de envío batea",
        type: "feat" as const,
        items: [
          "Los administradores pueden revertir un envío batea finalizado de vuelta al estado En curso, limpiando la fecha de finalización y restaurando el último viaje.",
        ],
      },
      {
        title: "Web — Modificar móvil desde Viajes y Envíos",
        type: "feat" as const,
        items: [
          "Nuevo panel \"Modificar móvil\" en los formularios de creación y edición de viajes y en el detalle de envío: permite cambiar el chofer o el semi asignado al móvil de forma persistente, sin afectar al móvil en sí.",
          "Si hay cambios pendientes en el panel al guardar el viaje, se muestra un diálogo de confirmación para aplicar o descartar la modificación del móvil.",
        ],
      },
    ],
  },
  {
    version: "v0.43.0",
    date: "2026-08-17",
    sections: [
      {
        title: "Web — Contratos con clientes: semanas, eventos y detalle",
        type: "feat" as const,
        items: [
          "Nueva sección Relaciones → Contratos: registra contratos de transporte por cliente con línea de trabajo (batea, nacional, internacional), objetivo mensual en toneladas y peso de referencia por camión. CRUD completo con búsqueda, paginación y soft delete con restauración.",
          "El detalle del contrato permite gestionar semanas: cada semana tiene etiqueta, fechas de inicio y fin, y toneladas objetivo. Las semanas se pueden generar automáticamente para un mes completo (lunes a domingo) o crear manualmente.",
          "Dentro de cada semana se registran eventos de ajuste (clima, desperfecto mecánico, chofer, causa externa u otro) con la cantidad de toneladas afectadas, fecha y notas.",
          "Panel de envíos por semana: expande cada semana para ver los envíos cargados en ese período con remito, estado, toneladas de carga y descarga, chofer, camión y semi.",
          "Estadísticas mensuales: selector de año y mes que muestra el objetivo mensual, toneladas reales acumuladas, camiones necesarios restantes y progreso por semana con barra de avance segmentada y codificada por color.",
          "Por semana se calculan automáticamente: toneladas reales (de envíos cargados), ajuste por eventos externos, camiones necesarios según el peso de referencia del contrato y camiones faltantes.",
        ],
      },
    ],
  },
  {
    version: "v0.42.0",
    date: "2026-08-12",
    sections: [
      {
        title: "Web — Sistema de roles y permisos (RBAC)",
        type: "feat" as const,
        items: [
          "Nueva página /system/roles con la matriz visual de permisos: muestra qué puede ver y hacer cada rol (admin, operador, gestor de flota, chofer) en cada sección del sistema.",
          "Los permisos ahora se gestionan desde un único archivo centralizado; cambiar un valor afecta automáticamente la visibilidad de botones, el sidebar y los guards de rutas — sin tocar componentes individuales.",
          "Nuevo concepto de superadmin vía variable de entorno (SUPERADMIN_EMAILS): bypass total de permisos sin cambio de rol en la base de datos.",
          "Nueva guía técnica de roles y permisos en /docs/roles-permisos.",
        ],
      },
      {
        title: "Web — Envíos: campo finalizedAt",
        type: "feat" as const,
        items: [
          "Los envíos batea registran ahora la fecha y hora exacta de finalización (finalizedAt) al completar el último hito. Visible en el detalle del envío.",
        ],
      },
      {
        title: "App Mobile — Hito 6: Finalizar viaje batea",
        type: "feat" as const,
        items: [
          "Nuevo hito final en el flujo batea: después de registrar la descarga en destino, el conductor ingresa Ton. brutas y Tara; las toneladas de descarga netas se calculan automáticamente (Brutas − Tara).",
          "Botón 'Finalizar viaje' que completa el tramo y marca el envío como finalizado. El botón queda bloqueado hasta que se ingresen ambos valores.",
          "El panel de carga de datos (N° Remito y Ton. carga) ahora solo se muestra entre el hito 'Carga en origen' y 'Llegada al destino', evitando duplicados con el panel de descarga.",
        ],
      },
      {
        title: "App Mobile — Conversión automática Toneladas / Kg",
        type: "fix" as const,
        items: [
          "El conductor ingresa todos los pesos en toneladas; la app convierte automáticamente a kg al guardar en la base de datos y vuelve a convertir a toneladas al mostrar los valores. Evita errores de escala entre lo que ve el chofer y lo almacenado.",
        ],
      },
      {
        title: "App Mobile — UX batea: destinos y toneladas requeridas actualizados",
        type: "fix" as const,
        items: [
          "Los destinos finales batea se actualizaron a: Laje9, Laje20, Laje29, Laje31 y Laje41.",
          "El botón de descarga en destino permanece deshabilitado hasta que el conductor ingrese las toneladas de descarga.",
        ],
      },
    ],
  },
  {
    version: "v0.41.0",
    date: "2026-08-12",
    sections: [
      {
        title: "Viajes — Camión y semi por viaje",
        type: "feat" as const,
        items: [
          "La tabla de viajes ahora muestra dos columnas separadas: \"Camión / Semi\" con la patente y número de unidad del camión y del semirremolque, y \"Chofer\" con el nombre del conductor.",
          "Cada viaje puede tener asignados directamente su propio chofer, camión y semi, independientemente del móvil. Estas asignaciones se guardan en la base de datos y quedan históricas.",
          "El formulario de creación y edición incluye tres selectores: Chofer, Camión y Semi/Tráiler. Al elegir un móvil, los tres campos se precargan automáticamente con los datos actuales de ese móvil y pueden ajustarse manualmente.",
        ],
      },
    ],
  },
  {
    version: "v0.40.0",
    date: "2026-08-11",
    sections: [
      {
        title: "SmartGT — Exportar documentos de carga: ID unificado por viaje",
        type: "fix" as const,
        items: [
          "Cuando dos o más CRTs del mismo chofer y cliente se fusionan en un único viaje (Regla 5 — clientes no Mondelez), los documentos de carga de todos los CRTs del grupo ahora comparten el ID EXTERNO del primero. Antes cada CRT conservaba su propio ID, generando inconsistencia con el archivo de datos de carga que ya los colapsaba a una sola fila.",
        ],
      },
    ],
  },
  {
    version: "v0.39.0",
    date: "2026-08-06",
    sections: [
      {
        title: "App Mobile — Carga de datos desde la lista de viajes (batea)",
        type: "feat" as const,
        items: [
          "Nuevo panel colapsable en el card de viaje batea para ingresar N° Remito, Ton. carga y Ton. descarga sin abrir el detalle. Guarda automáticamente al salir de cada campo y muestra un checkmark verde de confirmación.",
          "Lista de documentos adjuntos visible directamente en el card: muestra nombre, notas y fecha de cada archivo con ícono según tipo (imagen, PDF, planilla). Se actualiza automáticamente al cerrar el modal de docs.",
          "Botón azul 'Remitos y Documentos' en el card batea para abrir el modal de documentos sin entrar al detalle del viaje.",
        ],
      },
      {
        title: "App Mobile — Inicio de viaje con datos de carga (no batea)",
        type: "feat" as const,
        items: [
          "Al tocar 'Comenzar viaje' en viajes nacionales e internacionales se despliega un formulario inline para ingresar Ton. carga, Ton. descarga y N° Remito antes de iniciar (todos opcionales).",
        ],
      },
      {
        title: "App Mobile — Toneladas de descarga requeridas para batea",
        type: "feat" as const,
        items: [
          "El botón 'Descarga en destino' queda deshabilitado hasta que el conductor ingrese las toneladas de descarga. Se muestra un aviso naranja indicando el requisito.",
        ],
      },
      {
        title: "App Mobile — Destinos y ventanas horarias batea actualizados",
        type: "feat" as const,
        items: [
          "Los destinos finales batea se actualizaron a: Laje9, Laje20, Laje29, Laje31 y Laje41.",
          "Las ventanas de carga pasan de 5 turnos de 3 horas a 6 turnos de 3h30 con cobertura de las 24 horas del día.",
        ],
      },
      {
        title: "Web — Partes: líneas de trabajo y coordenadas GPS",
        type: "feat" as const,
        items: [
          "Las partes (remitentes / destinatarios) ahora tienen líneas de trabajo asignables (Nacional, Internacional, Batea), visibles como badges de colores en la tabla.",
          "Nuevo campo de coordenadas GPS (latitud / longitud) con preview de Google Maps en tiempo real al editar. El pin de ubicación en la tabla abre Google Maps directamente.",
          "Eliminar una parte que está asociada a un envío activo ahora muestra un error con el N° de envío bloqueante en lugar de fallar silenciosamente.",
        ],
      },
      {
        title: "Web — Tabla de envíos: columna de toneladas unificada",
        type: "feat" as const,
        items: [
          "Las columnas T. carga y T. desc. se fusionaron en una sola que muestra ambos valores apilados (↑ carga, ↓ descarga) y la diferencia en color: rojo si hay merma, verde si hay ganancia, gris si son iguales o el resultado redondea a 0,00 t.",
          "El badge de estado del remito (normal / validado / reclamado) ahora aparece debajo del número en lugar de al lado.",
        ],
      },
      {
        title: "Web — Documentación: estados de envíos y viajes",
        type: "feat" as const,
        items: [
          "Nueva página de documentación interna (/docs/shipment-trip-statuses) que describe todos los estados de envíos y viajes, quién los asigna, qué hito los dispara en la app y una tabla de combinaciones con su significado operativo.",
        ],
      },
    ],
  },
  {
    version: "v0.38.0",
    date: "2026-08-06",
    sections: [
      {
        title: "Web — Seguimiento público de envíos",
        type: "feat" as const,
        items: [
          "Nueva página pública (/public/c/:clientId/s/:shipmentId) accesible sin login para que los clientes rastreen su envío en tiempo real.",
          "Muestra estado del envío, nombre del chofer, patente del camión, información de la carga y timestamps de cada hito completado.",
          "El acceso valida que el envío pertenezca al cliente indicado en la URL — no hay filtración de datos entre clientes.",
        ],
      },
      {
        title: "Web — Kanban de hitos por línea de trabajo",
        type: "feat" as const,
        items: [
          "Nuevas páginas de cargas por línea: Batea, Nacional e Internacional, cada una con su propio Kanban de hitos.",
          "Las cards se pueden arrastrar entre columnas para registrar el avance del envío (p. ej. Carga en origen → En tránsito → Llegada al destino).",
          "Cada card muestra chofer, operador, patentes y un buscador por remitente, destinatario, cliente, chofer o ID interno.",
          "En el listado de Envíos se puede alternar entre vista tabla y vista Kanban con un toggle; la preferencia se guarda entre sesiones.",
        ],
      },
      {
        title: "Web — Detalle de envío ampliado",
        type: "feat" as const,
        items: [
          "Nueva sección de Valorizaciones en el detalle del envío: permite registrar conceptos (cruce, descarga, estadía, flete, demora, gastos de frontera) con cantidad, monto, notas y archivo adjunto.",
          "Nueva sección de Documentos de viaje: subida y gestión de archivos (remitos, planillas, etc.) con edición de comentario por fila.",
          "Se pueden visualizar el plan de viaje y la verificación técnica enviados por el chofer desde la app, en modales dedicados dentro del detalle.",
        ],
      },
      {
        title: "Web — Estadísticas de flota en el dashboard",
        type: "feat" as const,
        items: [
          "Nueva sección de estadísticas en el dashboard: conteo de choferes, semis, camiones, equipos de frío y móviles, divididos en asignados / sin asignar / inactivos.",
          "Los administradores pueden filtrar por operador; los operadores ven automáticamente solo su propia flota.",
        ],
      },
      {
        title: "Web — Importaciones SmartGT: selector de formato y logs",
        type: "feat" as const,
        items: [
          "Nueva página de historial de importaciones SmartGT: muestra cada intento con usuario, nombre de archivo, tamaño, versión de formato y conteo de registros importados / actualizados / sin cambios.",
          "Desde el historial se puede descargar el archivo CSV original de cada importación.",
          "Selector de versión de formato (v1 / v2) en el formulario de importación, con v2 como valor predeterminado (formato actual de ucarga).",
          "Soporte completo del nuevo formato v2 de ucarga: agrega la columna 'RG / C.I Motorista' en el índice 14 y reestructura la sección de fechas aduaneras (86 columnas en total).",
          "Nueva página de documentación interactiva en /docs/smart-gt-v2-format con la tabla de los 86 campos, su mapeo en DB y cuáles se ignoran.",
        ],
      },
      {
        title: "App Mobile — Verificación técnica pre-viaje",
        type: "feat" as const,
        items: [
          "Nuevo wizard de verificación técnica: el chofer recorre una lista de ítems del camión y del semi (frenos, neumáticos, luces, documentación, etc.) y marca cada uno como OK o con observación.",
          "La verificación queda guardada en el envío y es visible desde el detalle en la web.",
          "Si el chofer intenta guardar con ítems sin verificar, aparece un modal de confirmación que le permite volver a completarlos o guardar igual.",
        ],
      },
      {
        title: "App Mobile — Documentos de envío desde el móvil",
        type: "feat" as const,
        items: [
          "Los choferes pueden ver, subir y eliminar archivos adjuntos de un envío directamente desde la app (imágenes, PDFs, planillas).",
          "Cada documento muestra nombre, fecha de subida y permite agregar un comentario.",
        ],
      },
    ],
  },
  {
    version: "v0.37.0",
    date: "2026-08-02",
    sections: [
      {
        title: "API — Reorganización de rutas en subcarpetas",
        type: "feat" as const,
        items: [
          "Las rutas de la API se reorganizaron en subcarpetas por dominio: cargo/, providers/, shipments/ y smart-gt/.",
          "providers/ agrupa drivers, unidades, móviles y documentos de choferes.",
          "shipments/ agrupa envíos y valorizaciones.",
          "smart-gt/ agrupa la integración SmartGT, exportación y sus tests.",
          "cargo/ agrupa tipos de carga y productos.",
          "Las rutas HTTP y ApiType no cambiaron — el cambio es puramente estructural.",
          "Nuevos tests de schemas y validación de body sin DB para cargo, shipments y providers.",
        ],
      },
    ],
  },
  {
    version: "v0.36.0",
    date: "2026-08-02",
    sections: [
      {
        title: "App Mobile — Flujo completo para viajes Batea",
        type: "feat" as const,
        items: [
          "Nuevo flujo secuencial de 5 hitos para viajes Batea: Llegada al origen → Carga en origen → Comenzar viaje → Llegada al destino → Descarga en destino.",
          "Cada hito registra un timestamp en la tabla shipments y desbloquea el siguiente paso automáticamente.",
          "El hito 'Carga en origen' requiere que el chofer cargue la planificación del viaje previamente.",
          "Botón 'Confirmar viaje' visible en estado Planificado para que el chofer confirme el envío antes de iniciar el flujo.",
          "Ventana de llegada estimada (44–48 hs desde la salida) con countdown en vivo que cambia de verde a rojo al cumplirse las 44 hs.",
          "Selector de destino final editable por el chofer (Añelo / Pozo Cercano / Pozo Lejano / Sin asignar), guardado en shipments.final_destination.",
          "Panel de ventana de carga en la card: muestra el rango horario configurado y la fecha de salida proyectada.",
          "Badge 'Batea' en la card del chofer; se oculta el badge de línea redundante (Nacional).",
          "La pestaña 'Próximos' solo muestra viajes en estado planificado.",
        ],
      },
      {
        title: "App Mobile — Refactoring de componentes",
        type: "feat" as const,
        items: [
          "Lógica de hitos Batea extraída a useBateaHandlers (hook) y BateaTripActions (componente), separando responsabilidades de HomeScreen y TripCard.",
          "FinalDestinationPicker extraído como componente independiente reutilizable.",
          "Nueva página de documentación: App Mobile — Flujo Batea.",
        ],
      },
    ],
  },
  {
    version: "v0.35.0",
    date: "2026-07-25",
    sections: [
      {
        title: "Móviles — modales de detalle expandidos",
        type: "feat" as const,
        items: [
          "Modal del Semi: muestra patente, tipo, marca, año, VIN, dimensiones (tara, largo, ancho, alto), equipo de frío con sensores, estado de mapeo y calibración, y observaciones.",
          "Modal del Camión: muestra N° de unidad, tipo, marca/modelo, año, patente, combustible, VIN, N° de motor, tara, ejes y observaciones.",
          "Modal del Equipo de Frío: nuevo modal accesible desde el badge ❄ EQxxx en la columna Semi. Muestra código interno, descripción y observaciones.",
          "El badge ❄ EQxxx ahora es clickeable y abre el modal del equipo de frío.",
        ],
      },
      {
        title: "Móviles — lógica de estado activo/incompleto",
        type: "feat" as const,
        items: [
          "Un móvil es 'Activo' solo si tiene chofer, camión y semi asignados, todos sin eliminar y con estado activo.",
          "Si cualquiera de los tres recursos fue eliminado (soft-delete), el móvil pasa a 'Incompleto' automáticamente.",
          "Los dots de color (●) en las columnas Chofer, Camión y Semi ahora muestran rojo si el recurso fue eliminado, incluso si su estado interno era activo.",
          "El badge en la tabla cambió de 'Inactivo' a 'Incompleto' para reflejar mejor la situación.",
        ],
      },
      {
        title: "Móviles — filtro por estado",
        type: "feat" as const,
        items: [
          "Nuevo filtro 'Estado' en la barra de filtros: botones 'Activos' e 'Incompletos' para filtrar la tabla rápidamente.",
        ],
      },
      {
        title: "Móviles — avatar de responsable",
        type: "feat" as const,
        items: [
          "La columna Responsable ahora muestra un avatar circular con las iniciales del operador en lugar del nombre completo. El color es estable por operador. El nombre completo aparece al pasar el mouse.",
        ],
      },
      {
        title: "Seeders de flota — nuevos operadores",
        type: "feat" as const,
        items: [
          "Nuevos seeders de importación de flota: op-matias-v (Transjoven, Internacional), op-maxi-cdc (CDC, Nacional), op-saul-cdc (CDC, Batea), op-santiago-cdc (CDC, Batea).",
          "Los seeders leen columnas MAPEO (→ isMapped + observaciones del trailer) y CALIBRACION (→ isCalibrated del trailer).",
          "Si un chofer no se encuentra por DNI, el móvil se crea igual sin chofer asignado (caso de reemplazo por enfermedad).",
          "Trailers tipo PAQUETERO o sin código INTERNO guardan el nombre del equipo en observaciones en lugar de intentar linkear una unidad inexistente.",
        ],
      },
      {
        title: "Choferes — corrección al guardar estado",
        type: "fix" as const,
        items: [
          "El campo 'Estado del chofer' y 'Operador asignado' ahora se guardan correctamente al editar un chofer (antes se ignoraban al enviar el formulario).",
        ],
      },
    ],
  },
  {
    version: "v0.34.0",
    date: "2026-07-25",
    sections: [
      {
        title: "Documentos de carga — comportamiento corregido para invoice 'A INFORMAR'",
        type: "fix" as const,
        items: [
          "Cuando el invoice de un CRT dice 'A INFORMAR' (o cualquier variante), ahora solo se omite la fila FC. La fila CR se mantiene y muestra 'FC.: A INFORMAR' en la columna DESCRIPCION.",
          "Corrección técnica: el chequeo se realiza sobre el string crudo del invoice antes de parsearlo. Si se chequeaba después del parseo, 'A INFORMAR' quedaba dividido en CEN. EMISOR='A IN' / NUMERO='FORMAR' y el filtro no funcionaba.",
        ],
      },
      {
        title: "Nueva documentación: Reglas de exportación — Documentos de Carga",
        type: "feat" as const,
        items: [
          "Nueva página de documentación (/docs/reglas-documentos-carga) con descripción completa de todas las reglas que aplican al export de documentos de carga: exclusiones en orden, parseo del número CRT, parseo de factura AR (con y sin guión), y tabla resumen de combinaciones.",
        ],
      },
    ],
  },
  {
    version: "v0.33.0",
    date: "2026-07-24",
    sections: [
      {
        title: "Exportar documentos de carga — excluir CEN. EMISOR 'A INFORMAR'",
        type: "fix" as const,
        items: [
          "Los CRTs cuyo CEN. EMISOR resulte 'A INFORMAR' o 'AINFORMAR' ya no se incluyen en el export de documentos de carga. Se omiten las dos filas del CRT (CR + FC).",
        ],
      },
    ],
  },
  {
    version: "v0.32.0",
    date: "2026-07-03",
    sections: [
      {
        title: "SmartGT — Import de datos duplicados con CRT Confirmado",
        type: "fix" as const,
        items: [
          "Cuando un import encuentra un registro que ya existe en el sistema: si el CRT está en estado 'Confirmado', sólo se actualizan los campos de fecha (21 campos: llegada, salida, aduanas, etc.). Los datos operativos (chofer, camión, carga, etc.) se preservan.",
          "Si el CRT no está confirmado, el import continúa actualizando todos los campos normalmente.",
          "Esto protege datos confirmados y manuales de ser sobrescritos por imports automáticos.",
        ],
      },
      {
        title: "Unidades — filtro por tipo y SGT",
        type: "fix" as const,
        items: [
          "Al cambiar el filtro de tipo de unidad (ej: 'Refrigerado (Q)') o SGT, la tabla ahora automáticamente vuelve a la página 1 (antes quedaba en la página anterior, mostrando vacío).",
          "Se agregó el campo sgtCodes al listado de unidades en la API — necesario para el filtro SGT que diferencia 'Con SGT' vs 'Sin SGT'.",
        ],
      },
    ],
  },
  {
    version: "v0.31.0",
    date: "2026-06-23",
    sections: [
      {
        title: "Exportar documentos de carga — sin ID VIAJE",
        type: "fix" as const,
        items: [
          "Se eliminó la columna ‘ID VIAJE’. El archivo ahora tiene 8 columnas: ID EXTERNO, FILIAL, FECHA, TIPO, LETRA, CEN. EMISOR, NUMERO, DESCRIPCION.",
        ],
      },
      {
        title: "Exportar datos de carga — ORIGEN/DESTINO alineados",
        type: "fix" as const,
        items: [
          "ORIGEN y DESTINO ahora usan la misma lógica que ‘datos de viaje’: se muestra el código ucarga de la ciudad cuando existe (fallback al texto original). Corrige mapeos invertidos/inconsistentes.",
        ],
      },
      {
        title: "Reutilización de lógica en exports",
        type: "fix" as const,
        items: [
          "ORIGEN/DESTINO unificados en un helper interno — misma fuente de verdad para ambos exports.",
        ],
      },
      {
        title: "Rutas — límite de 10 caracteres",
        type: "fix" as const,
        items: [
          "Validación y DB alineadas a 10 caracteres para el código de ruta. UI, Zod y DB ahora leen de una constante compartida (ROUTE_CODE_MAX). Requiere generar y aplicar migración en DB.",
        ],
      },
    ],
  },
  {
    version: "v0.30.0",
    date: "2026-06-22",
    sections: [
      {
        title: "Exportar datos de carga — ID VIAJE al final",
        type: "feat" as const,
        items: [
          "La columna ‘ID VIAJE’ se movió al final del archivo (columna 18). Esto evita romper integraciones que esperan las primeras 17 columnas con la estructura original.",
        ],
      },
      {
        title: "SmartGT — Regla 3 y UI",
        type: "feat" as const,
        items: [
          "Excepción Regla 3 aplicada en la lista: cuando la transportadora es TransJovem y el propietario del camión es CDC/Kaizen/TJ, ya no se muestra el ícono ⚠️ en la columna Cliente (aunque no esté matcheado).",
          "Se agrega un badge compacto ‘TJ’ junto al cliente en esos casos para indicar que en los exports el cliente se reemplaza por TransJovem.",
          "Recalcular alertas alinea la lógica con esta excepción: no penaliza ‘cliente sin mapeo’ para TransJovem + owner CDC/Kaizen/TJ.",
        ],
      },
      {
        title: "SmartGT — filtros",
        type: "fix" as const,
        items: [
          "El filtro ‘Estado’ ahora inicia en ‘Abierto’ por defecto al ingresar a SmartGT.",
        ],
      },
    ],
  },
  {
    version: "v0.29.0",
    date: "2026-06-19",
    sections: [
      {
        title: "SmartGT — mejoras de exportación y rutas rápidas",
        type: "feat" as const,
        items: [
          "Valorización agrupada por viaje: cuando 2 CRTs pertenecen al mismo viaje (mismo chofer y fechas próximas), la valorización ahora genera una sola fila de conceptos en lugar de duplicarlos.",
          "Concepto KM en valorización ya no requiere estado Confirmado: se genera para cualquier CRT con ruta mapeada y fecha de llegada a coleta, independientemente del estado.",
          "Celda KM con ruta faltante es ahora clickeable: el ícono ⚠️ abre un diálogo para crear la ruta directamente desde SmartGT, con origen y destino pre-cargados y código/nombre auto-generados.",
          "Conexión entre destinos: el par de CRTs se ordena automáticamente por fecha de llegada (el más viejo primero) para derivar correctamente origen y destino del CRT puente.",
        ],
      },
      {
        title: "Formularios — validaciones y límites",
        type: "fix" as const,
        items: [
          "Campo Código limitado a 10 caracteres en todos los formularios (choferes, clientes, unidades, rutas, conceptos de pago) — alineado al constraint real de la base de datos.",
          "PV Cuenta es ahora obligatorio en el formulario de choferes.",
          "Kilometraje obligatorio en el diálogo de crear ruta rápida: no se puede guardar con 0 ni vacío.",
        ],
      },
      {
        title: "Código — refactor SmartGTPage",
        type: "fix" as const,
        items: [
          "SmartGTPage dividido en módulos: types.ts (interfaces), utils.ts (helpers puros), SmartGTComponents.tsx (sub-componentes), QuickRouteDialog.tsx (dialog autocontenido). El archivo principal pasó de 3163 a 2521 líneas.",
        ],
      },
    ],
  },
  {
    version: "v0.28.0",
    date: "2026-06-17",
    sections: [
      {
        title: "Choferes, Clientes y Unidades — mejoras de usabilidad",
        type: "feat" as const,
        items: [
          "Toasts de confirmación al crear, actualizar, eliminar o restaurar choferes, clientes y unidades.",
          "Búsqueda por código SGT en la tabla de choferes y clientes: ahora podés encontrar un registro escribiendo su código SGT directamente en el buscador.",
          "El filtro de estado 'activos' en la tabla de choferes ahora filtra correctamente excluyendo registros eliminados.",
          "El botón Restaurar se deshabilita mientras la petición está en curso, evitando restauraciones duplicadas por doble-click.",
        ],
      },
      {
        title: "Alertas en CRTs — Caso 4: Choferes Fleteros CDC",
        type: "feat" as const,
        items: [
          "Los CRTs con prefijos CDC (AR180, BR180, AR244, BR244, PY611, AR611) ya no generan alerta por chofer inexistente: en los exports estos choferes se reemplazan automáticamente con 'FLETEROS VARIOS', por lo que la alerta no era relevante.",
          "El ícono de advertencia ⚠️ junto al nombre del chofer tampoco se muestra en los detalles expandidos de un CRT CDC, manteniendo la interfaz consistente con el cálculo de alertas.",
          "Nueva página de documentación disponible en Docs → Cálculo de Alertas en CRTs, con la lógica completa de validación, ejemplos comparativos y descripción de la excepción CDC.",
        ],
      },
      {
        title: "Documentación — ancho completo",
        type: "feat" as const,
        items: [
          "Todas las páginas de documentación ahora usan el ancho completo de la pantalla.",
        ],
      },
      {
        title: "Infraestructura y Calidad",
        type: "fix" as const,
        items: [
          "Tests de funciones puras del API (trip-data, smart-gt) ya no requieren DATABASE_URL para ejecutarse: la conexión a la base de datos y las variables de entorno se inicializan de forma lazy.",
          "Nuevos tests para las reglas de prefijos CDC/TJ (isCDCFleteroPrefix, isTJFleteroPrefix) y los helpers de formulario (toIntOrNull, toStrOrNull, toUuidOrNull).",
          "Los helpers de conversión de formulario (toIntOrNull, toStrOrNull, toUuidOrNull) y la constante API_URL ahora viven en un único módulo compartido lib/mutation-utils, eliminando duplicación entre hooks.",
          "La constante CDC_FLETERO_PREFIXES vive exclusivamente en packages/shared y es reutilizada por el API y el frontend, eliminando el riesgo de desincronización.",
        ],
      },
    ],
  },
  {
    version: "v0.27.0",
    date: "2026-06-16",
    sections: [
      {
        title: "Monedas y Conceptos de Pago",
        type: "feat" as const,
        items: [
          "Nueva entidad Monedas: gestión de códigos ISO y nombres de monedas (ej. RS Reales, PE Pesos).",
          "Nueva entidad Conceptos de Pago: gestión de tarifas unitarias por concepto con soporte para múltiples monedas y precisión decimal de hasta 4 dígitos.",
          "Carga inicial de datos: seeder con 23 conceptos de pago estándar (Fleteros, Capacitaciones, Comidas, Cruces, etc.) y monedas correspondientes.",
          "Accesos directos: nuevos módulos CRUD disponibles en el menú de Configuración para administradores y operadores.",
        ],
      },
      {
        title: "Interfaz de usuario — Sidebar",
        type: "feat" as const,
        items: [
          "Menús colapsables: todos los grupos de la barra lateral (Geografía, Viajes, Cargas, Flota, Relaciones, SmartGT) ahora inician colapsados para una navegación más limpia.",
          "Nuevos íconos: BanknoteIcon para conceptos de pago y CoinsIcon para monedas.",
        ],
      },
      {
        title: "Infraestructura y Calidad",
        type: "feat" as const,
        items: [
          "Soporte para precisión numérica: incorporación del tipo 'numeric' en el esquema de base de datos para cálculos monetarios precisos.",
          "Nuevos tests de validación: tests de esquema Zod para las nuevas entidades de monedas y conceptos de pago.",
        ],
      },
    ],
  },
  {
    version: "v0.26.0",
    date: "2026-06-13",
    sections: [
      {
        title: "Corregir alertas de mapeo desde SmartGT",
        type: "feat" as const,
        items: [
          "Los campos Chofer, Camión y Carreta sin mapeo ahora son clickeables: el ícono ⚠️ abre un panel de corrección directamente desde la tabla de detalles del CRT.",
          "Chofer sin mapeo: abre un Sheet lateral con buscador de choferes registrados. Seleccioná el chofer y hacé click en Vincular — el nombre del SmartGT se agrega automáticamente a los sgt_codes del chofer para que el matching funcione en futuros imports.",
          "Camión y Carreta sin mapeo: abre la página completa de Unidades en un modal con la patente pre-cargada en el buscador. Permite editar la unidad existente o crear una nueva con la patente y código ya completados.",
          "Hover visual en botones de alerta: fondo ámbar suave al pasar el mouse sobre cualquier ⚠️ clickeable.",
        ],
      },
      {
        title: "Provincias — corrección de datos",
        type: "fix" as const,
        items: [
          "6 provincias estaban asignadas al país incorrecto: AFE y VIT pasaron de UY a CL y BR respectivamente; PUE y SAN de UY a CL; PAR y PR de CL a PY y BR.",
          "Nombres corregidos: CON/CL era Concepción → Las Condes; CAU/CL era La Araucanía → Cautín; PUE/CL era Pudahuel → Puerto Montt; LIM/PY era Limón → Limpio; MTG/BR era Mato Grosso → Mato Grosso do Sul; CAP/AR era Capital Federal → Ciudad Aut. de Bs.As.",
          "Nuevas provincias agregadas: San Felipe (CL), Punta Arenas (CL), Región Metropolitana (CL), Concordia (PY), Vitória (BR), La Rioja / Catamarca (AR).",
          "Duplicados eliminados del seeder: SC/BR Santa Cruz y SP/BR Santa Rosa sobreescribían los valores correctos Santa Catarina y São Paulo.",
        ],
      },
      {
        title: "Infraestructura — migraciones y Turbo",
        type: "fix" as const,
        items: [
          "db:fresh ahora usa db:migrate en lugar de db:push: las secuencias PostgreSQL y otros objetos SQL manuales se crean automáticamente en cada reset de dev.",
          "db:reset dropea los schemas public + drizzle para que db:migrate arranque con el journal limpio.",
          "Scripts db:* de la raíz del monorepo corregidos para usar --filter=@cdc-sccr/api (el filtro 'api' no funcionaba con Turbo).",
          "Tareas db:migrate y db:fresh agregadas al pipeline de turbo.json.",
        ],
      },
    ],
  },
  {
    version: "v0.25.0",
    date: "2026-06-11",
    sections: [
      {
        title: "Conexión entre destinos",
        type: "feat" as const,
        items: [
          "Nuevo botón 'Conexión entre destinos' en la barra de exportación: crea un CRT puente a partir de 2 CRTs seleccionados. Ciudad origen = destino del primer CRT, ciudad destino = origen del segundo. Chofer, camión y carreta del primer CRT; fecha de carga = fecha del primer CRT + 1 día; fecha de llegada a coleta = fecha del primer CRT + 1 día.",
          "Códigos generados automáticamente con secuencias PostgreSQL: CRT estilo C000000001 y ID externo estilo V000000001 — atómicos, sin riesgo de duplicados bajo carga concurrente.",
          "Diálogo de confirmación con preview del CRT que se creará y botón 'Intercambiar' para cambiar el orden de los CRTs.",
          "El botón se habilita solo con exactamente 2 CRTs seleccionados y muestra un badge X/2 como indicador visual.",
        ],
      },
      {
        title: "Regla 4 — Viajes fleteros actualizada",
        type: "feat" as const,
        items: [
          "Sub-caso TJ (AR572, BR572, CL572) + fletero: ahora se EXCLUYE de todos los exports (carga, viaje, documentos y valorización). Antes cambiaba el cliente a TransJoven.",
          "Sub-caso CDC ampliado: se agregan prefijos AR244, BR244, PY611, AR611 a los ya existentes AR180, BR180.",
          "Corrección: carrier 'TRANSPORTE C D C S A' (letras separadas por espacios) ahora se detecta correctamente como CDC en todas las reglas.",
          "Regla 4 ahora aplica también a documentos de carga y valorización (antes sin filtro fletero).",
        ],
      },
      {
        title: "Regla 5 — Merge de carga para clientes no-Mondelez",
        type: "feat" as const,
        items: [
          "Cuando la Regla 1 fusiona viajes (mismo chofer + cliente + ±N días), los clientes que no son Mondelez (legajo 175) ahora también colapsan sus cargas en 1 fila con BULTOS=1.",
          "Mondelez mantiene el comportamiento anterior: 1 fila de carga por CRT.",
          "Documentos de carga siempre generan 2 filas por CRT independientemente del cliente.",
          "Ejemplos: JUAN GERMAN GARAY (3 CRTs → 1 carga, American Transport), SILVIO ORLANDO NUÑEZ (2 CRTs → 1 carga, American Transport), CORREIA DE ANDRADE (2 CRTs → 1 carga, Danisco).",
        ],
      },
      {
        title: "Exportar documentos de carga — nueva columna",
        type: "feat" as const,
        items: [
          "Nueva columna 'ID EXTERNO' (col 7, última) en el export de documentos de carga, con el mismo valor GT+sourceId que usa el export de datos de carga.",
        ],
      },
      {
        title: "Tests",
        type: "feat" as const,
        items: [
          "21 nuevos tests en lib.test.ts: isCDCCarrier (incluyendo detección con espacios entre letras), isTJFleteroExcluded, isCDCFleteroPrefix, isTJFleteroPrefix, y MONDELEZ_FILE_ID — 62 tests en total, 0 fallos.",
        ],
      },
    ],
  },
  {
    version: "v0.24.0",
    date: "2026-06-09",
    sections: [
      {
        title: "Reglas de exportación — Viajes TransJoven y Fleteros",
        type: "feat" as const,
        items: [
          "Regla 3 — Viajes TransJoven: cuando la transportadora es TransJoven y el propietario del camión es CDC, el cliente se reemplaza por TransJoven (legajo 434) en datos de carga y valorización (250 CRTs afectados)",
          "Regla 4 — Viajes fleteros (Sub-caso A, CRT TJ: AR572/BR572/CL572): viaje asignado a FLETEROS VARIOS, carga a TransJoven — 70 CRTs principalmente de Nestlé y Kimberly Clark",
          "Regla 4 — Viajes fleteros (Sub-caso B, CRT CDC: AR180/BR180): viaje asignado a FLETEROS VARIOS, carga queda en cliente original — 5 CRTs (MARETEC, ALMACEN VETERINARIO, McCAIN)",
          "Panel Reglas de exportación: nuevas reglas 3 y 4 con checkbox y descripción, numeradas 1–4 tanto en el panel como en la documentación",
          "Orden default de la tabla SmartGT cambiado a llegada a coleta ASC (NULLS LAST) en frontend y backend",
        ],
      },
      {
        title: "Documentación de exportación rediseñada",
        type: "feat" as const,
        items: [
          "Página Mapeos para exportación reorganizada en dos tabs: Reglas de exportación y Mapeos de columnas — pantalla completa sin max-w",
          "Cada regla muestra una tabla de efectos por tipo de export y un bloque de ejemplos reales con CRTs actuales de la DB",
          "Regla 4 documenta los dos sub-casos (A: AR572/BR572 y B: AR180/BR180) con sus 75 CRTs de ejemplo",
          "Nota de regla pendiente (Descartar CRT) actualizada: los 5 CRTs AR180/BR180 con fletero ya están cubiertos por Sub-caso B",
        ],
      },
    ],
  },
  {
    version: "v0.23.0",
    date: "2026-06-09",
    sections: [
      {
        title: "Reglas de exportación Mondelez — CRT Confirmado y viaje único",
        type: "feat" as const,
        items: [
          "Regla 1 — CRT Confirmado excluido de todos los exports: si un CRT tiene estado Confirmado, no genera fila de viaje, carga ni documentos, independientemente de si está en la selección",
          "Regla 2 — Viaje único por chofer (Mondelez): cuando el chofer ya tiene un CRT Confirmado dentro de la ventana de ±N días (en la DB, fuera de la selección actual), se suprime la fila de viaje pero se exportan carga y documentos del CRT nuevo — la carga se incorpora al viaje ya existente",
          "Tipos de viaje eliminados: la entidad Trip Types fue removida completamente del sistema (tabla, API, seed, frontend y menú)",
          "Documentación actualizada: sección Reglas de exportación en Mapeos para exportación refleja las dos reglas con sus nombres del panel UI, tabla de escenarios y comportamiento por export",
        ],
      },
      {
        title: "Tests",
        type: "feat" as const,
        items: [
          "Nuevo archivo trip-data.test.ts: 17 tests unitarios para mergeGroups (agrupación básica, ventana de fechas, predicate de supresión por CRT Confirmado, estabilidad de orden) — sin dependencia de DB",
        ],
      },
    ],
  },
  {
    version: "v0.22.0",
    date: "2026-06-05",
    sections: [
      {
        title: "SmartGT — CRTs manuales",
        type: "feat" as const,
        items: [
          "Nuevo botón \u2018Nuevo CRT\u2019 en el panel de importación (solo admins): permite crear un CRT directamente desde la interfaz sin importar un archivo",
          "Formulario de creación con selects de cliente, ciudad remitente, ciudad destino y chofer (filtrados por sgt_codes para garantizar el matching automático)",
          "Campos de camión, trailer y fecha de carga para que el CRT pueda exportarse como dato de viaje",
          "Columna is_manual en la base de datos: los CRTs creados manualmente muestran un badge azul \u2018manual\u2019 bajo su número en la tabla",
          "CRTs manuales excluidos automáticamente de los exports \u2018Exportar datos de carga\u2019 y \u2018Exportar documentos de carga\u2019 (no tienen datos de carga válidos); sí se incluyen en datos de viaje y valorización",
        ],
      },
      {
        title: "SmartGT — mejoras en tabla y exportaciones",
        type: "feat" as const,
        items: [
          "Búsqueda por chofer en la barra de filtros: filtra CRTs cuyo detalle contenga el nombre ingresado (búsqueda parcial, case-insensitive)",
          "Ciudad remitente: cuando el campo está vacío se usa la ciudad de carga como fallback, marcada con una etiqueta discreta \u2018carga\u2019 en la celda",
          "Cambio de estado masivo: con uno o más CRTs seleccionados aparece un dropdown \u2018Cambiar estado\u2019 en la barra de selección que actualiza todos en un solo request",
          "Columna VIAJE NRO eliminada del export de datos de viaje (estaba siempre vacía)",
        ],
      },
      {
        title: "Correcciones",
        type: "fix" as const,
        items: [
          "Ruta GET /geo/routes/count registrada antes de GET /geo/routes/:id: el conteo de rutas en el dashboard ya no lanza error de UUID inválido",
          "Rutas PATCH /crts/bulk-status y POST /crts registradas antes de PATCH /crts/:id para evitar que Hono capture el segmento estático como parámetro",
        ],
      },
    ],
  },
  {
    version: "v0.21.0",
    date: "2026-06-03",
    sections: [
      {
        title: "SmartGT — importación formato ucarga 2026",
        type: "feat" as const,
        items: [
          "Soporte para el nuevo formato CSV de ucarga con 2 columnas vacías iniciales y CRT en la tercera columna",
          "Columnas de aduana divididas en variantes importación/exportación se resuelven automáticamente tomando el valor no nulo",
          "Fecha de descarga en formato americano M/D/YYYY detectada y convertida correctamente (campo Data Descarregamento)",
        ],
      },
      {
        title: "Países — nuevo campo código ucarga",
        type: "feat" as const,
        items: [
          "Campo \u2018Código ucarga\u2019 agregado a la tabla de países: NA=Argentina, BI=Brasil, CH=Chile, AD=Bolivia, PR=Paraguay, UR=Uruguay",
          "La columna TIPO VIAJE del export de datos de viaje ahora usa este código en lugar del código ISO",
          "Editable desde Geografía → Países",
        ],
      },
      {
        title: "SmartGT — exportaciones mejoradas",
        type: "feat" as const,
        items: [
          "Columnas de todos los exports renumeradas desde 1 en cada archivo independientemente",
          "Documentos de carga: nueva columna DESCRIPCION (col 6) con el invoice del CRT; formato de la fila FC diferenciado por país (Argentina vs Brasil/otros)",
          "Datos de carga: columna BULTOS ahora refleja la cantidad real de detalles del CRT en modo 1 fila/CRT",
          "Datos de viaje: regla \u2018viaje único por chofer\u2019 activa por defecto — fusiona CRTs del mismo chofer y cliente con llegada a colecta con diferencia máxima de 2 días en una sola fila; configurable desde el panel de reglas",
        ],
      },
      {
        title: "SmartGT — filtros, columnas y estados",
        type: "feat" as const,
        items: [
          "Filtro de rango de fechas por llegada a colecta (calendarios desde/hasta en la barra de filtros)",
          "Columna \u2018Lleg.\u2019 ordenable en la tabla de CRTs con la fecha de llegada a colecta",
          "Estado \u2018En valorización\u2019 agregado al flujo de trabajo: Abierto → Confirmado → En valorización → Valorizado",
          "Columnas Estado y Viaje separadas en columnas independientes en la tabla",
          "Búsqueda por CRT ahora también busca por producto",
        ],
      },
      {
        title: "Correcciones",
        type: "fix" as const,
        items: [
          "Filtro de fechas corregido para usar horario de Argentina (UTC-3) y ser consistente con los valores mostrados en la tabla",
          "Error \u2018too many clients\u2019 de PostgreSQL resuelto con límite explícito de pool de conexiones",
          "Estado \u2018Cerrado\u2019 y concepto \u2018Procesado\u2019 eliminados del flujo de trabajo",
        ],
      },
    ],
  },
  {
    version: "v0.20.0",
    date: "2026-05-30",
    sections: [
      {
        title: "SmartGT — exportación de CRTs seleccionados",
        type: "feat" as const,
        items: [
          "Cuatro botones de exportación en la barra de selección: Exportar datos de carga, Exportar datos de viaje, Exportar documentos de carga y Exportar valorización",
          "Cada botón genera y descarga un archivo CSV con la estructura de columnas del sistema SmartGT (columnas 1-17, 18-32, 43-47 y 33-37 respectivamente)",
          "El payload enviado al servidor es la lista de IDs de CRTs seleccionados; no hay límite de cantidad",
        ],
      },
      {
        title: "Tipos de carga — cargoDesc en crear y editar",
        type: "fix" as const,
        items: [
          "El campo Descripción (PE / NP) ya se envía correctamente al crear y editar un tipo de carga; antes se omitía en el body de la mutación",
          "Corregido error de build de TypeScript en Vercel: el tipo string no era asignable a \"PE\" | \"NP\" | null",
        ],
      },
    ],
  },
  {
    version: "v0.19.0",
    date: "2026-05-30",
    sections: [
      {
        title: "Landing y login",
        type: "feat" as const,
        items: [
          "Logo de la landing movido arriba del formulario de inicio de sesión para reforzar el branding",
        ],
      },
      {
        title: "SmartGT CRTs — layout de tabla más compacto",
        type: "feat" as const,
        items: [
          "Producto y Cliente ahora pueden ocupar varias líneas y usar más ancho disponible cuando la pantalla crece",
          "Remitente y Destinatario recuperaron un tamaño visual más legible y consistente",
        ],
      },
      {
        title: "Limpieza de textos técnicos",
        type: "fix" as const,
        items: [
          "Tests unificados en inglés y logs/comentarios técnicos de seeders normalizados a inglés",
        ],
      },
    ],
  },
  {
    version: "v0.18.0",
    date: "2026-05-29",
    sections: [
      {
        title: "SmartGT — alertas, estado de workflow y KM de ruta",
        type: "feat" as const,
        items: [
          "Columna warning_count pre-computada en smart_gt_crt: badge 🟢/🟡/🔴 según 0/1-2/3+ problemas",
          "Botón Recalcular alertas en el panel de importación; también se dispara automáticamente tras cada import",
          "Ordenamiento por alertas (asc/desc) en el header de la columna ⚠️",
          "Columna Estado de workflow: Abierto | Confirmado | Valorizado | Cerrado (editable desde el lápiz, filtrable)",
          "Columna Entrega: En curso / Finalizado según si todos los detalles tienen fecha de descarga",
          "Columna KM: ruta matcheada entre ciudad remitente y destino vía sgt_codes — link ↗ o ⚠️",
          "Detalle procesado editable desde el lápiz del detalle (Procesado Sí/No)",
        ],
      },
      {
        title: "Nuevas páginas de detalle",
        type: "feat" as const,
        items: [
          "Nueva página /geo/routes/:id con vista de recorrido Origen → KM → Destino",
          "Nueva página /drivers/:id con vista + edición de código, nombre y sgt_codes",
          "Nueva página /units/:id con vista + edición de patente, marca, año y sgt_codes",
          "Links en SmartGT (chofer, camión, carreta, ciudades, KM) apuntan al detalle del registro",
        ],
      },
      {
        title: "Tipos de carga — descripción de carga",
        type: "feat" as const,
        items: [
          "Nueva columna cargo_desc (PE | NP | null) en cargo_types",
          "Visible en la tabla y editable desde el sheet de edición",
        ],
      },
      {
        title: "Tabla CRTs — mejoras de UI",
        type: "feat" as const,
        items: [
          "Checkboxes por fila con selección múltiple y select-all con estado indeterminate",
          "Barra de acciones contextual al seleccionar: 4 botones de exportación (datos carga, viaje, documentos, valorización)",
          "Estado y Entrega mergeados en una sola columna compacta",
          "Cliente truncado a 2 líneas manteniendo el link clickeable",
        ],
      },
      {
        title: "Refactor: SQL duplicado eliminado",
        type: "fix" as const,
        items: [
          "recalculateAllWarnings() extraida como función helper — los dos lugares que la llamán ahora usan una única fuente de verdad",
        ],
      },
    ],
  },
  {
    version: "v0.17.0",
    date: "2026-05-26",
    sections: [
      {
        title: "SGT Codes — ciudades y unidades",
        type: "feat" as const,
        items: [
          "Nueva columna sgt_codes (text[], GIN index) en ciudades y unidades",
          "Seeder de ciudades: mapeo code → sgt_codes con 84 entradas explícitas (ej. TILCARA → TILCARA JUJUY)",
          "Seeder de productos: SGT_CODES_MAP explícito reemplaza el auto-generado desde el nombre",
          "Seeder de clientes: SGT_CODES_BY_FILE_ID mapea por legajo en lugar de copiar el nombre",
          "Columna Códigos SGT y filtro Con SGT / Sin SGT / Todos en: Ciudades, Unidades, Productos, Choferes, Clientes",
          "Prevención de duplicados: arrayOverlaps() en API para las 5 entidades — 409 con mensaje al intentar reusar un código",
        ],
      },
      {
        title: "Páginas de detalle — Ciudades, Choferes y Unidades",
        type: "feat" as const,
        items: [
          "Nueva página /geo/cities/:id con vista + edición de código, nombre y sgt_codes",
          "Nueva página /drivers/:id con vista + edición de código, nombre y sgt_codes",
          "Nueva página /units/:id con vista + edición de patente, marca, año y sgt_codes",
          "Nuevos endpoints GET /:id en geography, providers y units",
          "Links en SmartGT (chofer, camión, carreta, ciudades) apuntan al detalle del registro",
        ],
      },
      {
        title: "SmartGT — indicadores de ciudad en tabla principal",
        type: "feat" as const,
        items: [
          "Columnas Remitente y Destinatario muestran ciudad con link ↗ si está registrada o ⚠️ si no",
          "Destinatario usa unloadingCity como fallback cuando recipientCity es null",
          "Matching de ciudad por sgt_codes → name → code en orden de prioridad",
          "Camión/carreta: matching ampliado a code y license_plate además de sgt_codes",
        ],
      },
      {
        title: "Migraciones squash",
        type: "fix" as const,
        items: [
          "Migraciones 0001–0005 consolidadas en 0000_initial_schema.sql — instalaciones frescas usan un único archivo",
          "sgt_codes incluida directamente en el CREATE TABLE de cada entidad",
        ],
      },
    ],
  },
  {
    version: "v0.16.0",
    date: "2026-05-22",
    sections: [
      {
        title: "SGT Codes — vinculación automática con SmartGT",
        type: "feat" as const,
        items: [
          "Nueva columna sgt_codes (text[], GIN index) en clientes, productos, choferes y unidades",
          "Seeds actualizados: copian name/licensePlate normalizados (uppercase) como valor inicial",
          "Chips input en formularios de edición: Enter o coma para agregar, × para quitar, normalización automática a mayúsculas",
          "Migraciones 0002, 0003 y 0004 para las columnas y los índices GIN",
        ],
      },
      {
        title: "SmartGT CRTs — matching visual con entidades del sistema",
        type: "feat" as const,
        items: [
          "Columna Cliente: link azul si existe en clientes (por sgt_codes), ⚠️ ámbar si no está registrado",
          "Columna Producto: mismo patrón, link a /cargos/products/:id",
          "Detalles expandidos — Chofer, Camión, Carreta: link a /drivers o /units si matchea, ⚠️ si no",
          "Tabla CRT rediseñada: columnas Remitente y Destinatario con nombre + ciudad/país apilados",
          "Subconsultas correlacionadas con upper(trim(...)) = ANY(sgt_codes) usando los índices GIN",
        ],
      },
      {
        title: "Páginas de detalle — Clientes y Productos",
        type: "feat" as const,
        items: [
          "Nueva página /clients/:id con vista de lectura + Sheet de edición (admin) o solo lectura (operator)",
          "Nueva página /cargos/products/:id con vista de lectura + Sheet de edición (admin y operator)",
          "Botón Volver con navigate(-1): regresa exactamente a la pantalla anterior (CRT, lista, etc.)",
        ],
      },
      {
        title: "Productos — tipo de carga y filtros",
        type: "feat" as const,
        items: [
          "Relación cargoTypeId (FK nullable) en productos, Select en formulario de edición",
          "Columna Tipo en la tabla de productos con badge del tipo asignado",
          "Filtro por tipo de carga en la toolbar de productos",
        ],
      },
      {
        title: "Clientes — columna y filtro por legajo",
        type: "feat" as const,
        items: [
          "Columna Legajo (fileId) en la tabla de clientes",
          "Búsqueda extendida: el buscador ahora también filtra por número de legajo",
        ],
      },
      {
        title: "Choferes — columna y edición de códigos SGT",
        type: "feat" as const,
        items: [
          "Columna Códigos SGT en la tabla de choferes (hasta 2 chips + +N)",
          "Chips input en el Sheet de edición de choferes",
        ],
      },
    ],
  },
  {
    version: "v0.15.0",
    date: "2026-05-22",
    sections: [
      {
        title: "SmartGT — módulo de importación y gestión de CRTs",
        type: "feat" as const,
        items: [
          "Nueva sección SmartGT en el sidebar con acceso a /smart-gt",
          "Importación de CRTs desde archivo CSV o TXT (drag & drop + validación de tamaño y extensión)",
          "Tabla de CRTs con búsqueda, filtros por producto, transportista, paso fronterizo, estado, origen y destino",
          "Paginación y ordenamiento por columna en la tabla de CRTs",
          "Vista expandida de detalles por CRT (posiciones) con edición inline",
          "Modal de detalle completo con todos los campos de fechas y estado",
          "Endpoints API: GET /smart-gt (paginado + filtros), GET /:id/details, PATCH /:id, DELETE /:id, PATCH /details/:id, DELETE /details/:id",
          "Migración SQL: tablas smart_gt_crt y smart_gt_crt_details",
          "Lógica de upsert en importación: crea, actualiza o deja sin cambios según sourceId + CRT",
          "Reporte de warnings (campos cambiados) y errores de fila en el resultado de importación",
        ],
      },
      {
        title: "Clientes — schema Zod",
        type: "feat" as const,
        items: [
          "Nuevo schema packages/shared/src/schemas/clients.ts con createClientSchema, updateClientSchema y clientQuerySchema",
        ],
      },
      {
        title: "Docs — guía para agregar campos a un modelo existente",
        type: "feat" as const,
        items: [
          "Nueva página /docs/agregar-campos con guía completa de 8 secciones",
          "Cubre las 5 capas del flujo: schema.ts → migración SQL → Zod → API route → UI",
          "Incluye tabla de flujo, bloques de código, checklist de 16 ítems y 6 errores comunes documentados",
          "Card agregado en /docs (visible para rol admin)",
          "Archivo markdown complementario en docs/guia-agregar-campos-modelo-existente.md",
        ],
      },
    ],
  },
  {
    version: "v0.14.0",
    date: "2026-05-18",
    sections: [
      {
        title: "Sidebar — reorganización completa de la navegación",
        type: "fix" as const,
        items: [
          "Secciones Configuración y Ayuda movidas al fondo del sidebar con ícono delante (Settings2Icon / CircleHelpIcon), igual que el navSecondary anterior",
          "Configuración ahora agrupa Usuarios (solo admin) + Tipos de documento — el ítem Usuarios fue removido del navMain",
          "Ayuda agrupa Documentación + Guía de uso + Changelog — Documentación y Changelog removidos del navMain",
          "/help re-etiquetado como 'Guía de uso' dentro de la sección Ayuda",
          "navMain simplificado: solo Dashboard",
          "Sección 'Documentos' eliminada; Tipos de documento migrado a Configuración",
        ],
      },
      {
        title: "Choferes — columna Documento",
        type: "fix" as const,
        items: [
          "Columnas 'N° Documento' y 'Tipo doc' unificadas en una sola columna 'Documento'",
          "Muestra nombre del tipo de documento (badge) + número de documento lado a lado",
        ],
      },
      {
        title: "Migraciones — squash a archivo único",
        type: "fix" as const,
        items: [
          "0001_unit_types.sql, 0002_units.sql y 0003_drivers.sql absorbidos en 0000_initial_schema.sql",
          "Journal reducido a una sola entrada — el schema completo (15 tablas + enums + FKs) vive en un solo archivo",
        ],
      },
    ],
  },
  {
    version: "v0.13.0",
    date: "2026-05-18",
    sections: [
      {
        title: "Choferes — nueva entidad CRUD con seeder desde archivo",
        type: "feature" as const,
        items: [
          "Tabla drivers: code, name, file_id (nullable, 0→null), dni, doc_type_id (FK→doc_types nullable), doc_id, is_owned boolean, soft delete",
          "Migración incremental 0003_drivers.sql con FK nullable a doc_types",
          "Router providers.ts montado en /providers con sub-rutas /drivers/* — preparado para futuras entidades (transportistas, empresas)",
          "API /api/providers/drivers: GET (?deleted, ?isOwned), GET /count, POST, PATCH /:id (guard soft-delete), DELETE /:id (soft), PATCH /:id/restore",
          "Schemas Zod en @cdc-sccr/shared: createDriverSchema, updateDriverSchema, driverIdSchema, driverQuerySchema (filtra por deleted + isOwned)",
          "Seeder desde choferes-2026-0511.txt: 1078 registros, toTitleCase, file_id=0→null, doc_type lookup por código, guión→null en doc_id, is_owned TRUE/FALSE→boolean",
          "bun run db:seed:drivers integrado en pipeline db:seed",
          "Página /drivers: búsqueda por código/nombre/DNI/Doc ID, filtro Todos/Propios/Terceros + activos/eliminados, tabla (código, nombre, DNI, tipo doc, badge Propio/Tercero), Sheet formulario completo, Dialog delete, restore",
        ],
      },
      {
        title: "Sidebar — sección Proveedores",
        type: "feature" as const,
        items: [
          "Nueva sección 'Proveedores' colapsable entre Flota y Configuración",
          "Entrada 'Choferes' con UserIcon → /drivers",
        ],
      },
    ],
  },
  {
    version: "v0.12.0",
    date: "2026-05-18",
    sections: [
      {
        title: "Unidades — nueva entidad CRUD con seeder desde archivo",
        type: "feature" as const,
        items: [
          "Tabla units: code varchar(20) UNIQUE, name, unit_type_id (FK → unit_types), license_plate, vin, brand, engine_number, manufacture_year, deletedAt, timestamps",
          "Migración incremental 0002_units.sql con FK a unit_types",
          "API /api/units/: GET (?deleted, ?unitTypeId), GET /count, POST, PATCH /:id (guard soft-delete), DELETE /:id (soft), PATCH /:id/restore",
          "Schemas Zod en @cdc-sccr/shared: createUnitSchema, updateUnitSchema, unitIdSchema, unitQuerySchema (filtra por deleted + unitTypeId)",
          "Seeder desde apps/api/data/20160511/units-20260511.txt: 942 registros, toTitleCase en nombre, guión → null, parseDeletedAt DD/M/YYYY, cleanVin (+), parseYear (solo 1000-2100), skip si tipo desconocido",
          "Página /units: búsqueda por código/nombre/patente, filtro por tipo de unidad, filtro activas/eliminadas/todas, tabla resumida (código/nombre/tipo/patente), Sheet con formulario completo (todos los campos), Dialog delete, restore por fila",
        ],
      },
      {
        title: "Router units.ts — consolidación de unit-types + units",
        type: "feature" as const,
        items: [
          "Archivo unit-types.ts eliminado; routes/units.ts lo reemplaza conteniendo ambas entidades (patrón idéntico a trips.ts)",
          "Montado en /units en routes/index.ts — sub-rutas: /unit-types/* y bare /:id para unidades reales",
          "Frontend actualizado: todas las llamadas api['unit-types'] migradas a api['units']['unit-types'] y authorizedFetch paths actualizados",
        ],
      },
      {
        title: "Sidebar — nueva sección Flota",
        type: "feature" as const,
        items: [
          "Sección 'Flota' colapsable agregada entre Cargas y Documentos con TruckIcon (Tipos de unidad) y CarIcon (Unidades)",
          "Tipos de unidad movido de sección Viajes a Flota",
        ],
      },
    ],
  },
  {
    version: "v0.11.0",
    date: "2026-05-18",
    sections: [
      {
        title: "Tipos de unidad — nueva entidad CRUD completa",
        type: "feature" as const,
        items: [
          "Tabla unit_types en DB: code varchar(10) UNIQUE, name, deletedAt, timestamps — seeder con 7 tipos base",
          "API /api/unit-types: GET (?deleted), GET /count, POST, PATCH /:id (guard soft-delete), DELETE /:id (soft), PATCH /:id/restore",
          "Schemas Zod en @cdc-sccr/shared: createUnitTypeSchema (con .toUpperCase()), updateUnitTypeSchema, unitTypeIdSchema, unitTypeQuerySchema",
          "P\u00e1gina /unit-types: buscador, paginaci\u00f3n, filtro activos/eliminados/todos, Sheet create/edit, Dialog delete, restore por fila",
          "Seeder: A (Autom\u00f3viles), B (Buses / Micros), C (Camion / Tractor), M (Maquinaria), O (Otros), Q (EQUIPO DE FRIO), S (Semi / Acoplado)",
          "Sidebar: entrada 'Tipos de unidad' con TruckIcon en secci\u00f3n Viajes",
          "Migraci\u00f3n incremental 0001_unit_types.sql agregada al journal",
        ],
      },
    ],
  },
  {
    version: "v0.10.0",
    date: "2026-05-18",
    sections: [
      {
        title: "Tipos de documento — nueva entidad CRUD completa",
        type: "feature" as const,
        items: [
          "Tabla doc_types en DB: code varchar(10) UNIQUE, name, deletedAt, timestamps",
          "API /api/doc-types: GET (?deleted), GET /count, POST, PATCH /:id (con guard soft-delete), DELETE /:id (soft), PATCH /:id/restore",
          "Schemas Zod en @cdc-sccr/shared: createDocTypeSchema, updateDocTypeSchema, docTypeIdSchema, docTypeQuerySchema",
          "Página /doc-types: buscador, paginación, filtro Activos/Eliminados/Todos, Sheet create/edit, Dialog delete, restore por fila",
          "Seeder con 17 tipos base (DNI, CUIL, CUIT, CNPJ, RUT, Pasaporte, CI, LE, LC, CGC, RUC, NIT, Ninguno y variantes electrónicas)",
        ],
      },
      {
        title: "Sidebar — grupos colapsables",
        type: "feature" as const,
        items: [
          "Geografía, Viajes, Cargas y Documentos ahora son colapsables con chevron — estado inicial abierto",
          "Nueva sección \u2018Documentos\u2019 con entrada \u2018Tipos de documento\u2019",
        ],
      },
      {
        title: "Migraciones — squash a archivo único",
        type: "fix" as const,
        items: [
          "0000_initial_schema.sql ahora es el único archivo de migración: incluye todos los schemas (geo, trips, cargos, doc_types)",
          "Eliminados 0001_perfect_scarecrow.sql y 0001_cargo_types.sql (orphan)",
          "Journal actualizado a una sola entrada — snapshot regenerable con db:generate",
        ],
      },
    ],
  },
  {
    version: "v0.9.0",
    date: "2026-05-18",
    sections: [
      {
        title: "Tipos de viaje — nueva entidad CRUD completa",
        type: "feature" as const,
        items: [
          "Tabla trip_types en DB: code varchar(10) UNIQUE, name, deletedAt, timestamps — esquema alineado con service_types y cargo_types",
          "API /api/trips/trip-types: GET (filtro ?deleted), GET /count (solo activos), POST, PATCH /:id (con guard soft-delete), DELETE /:id (soft), PATCH /:id/restore — encadenado en tripsRouter",
          "Schemas Zod en @cdc-sccr/shared: createTripType, updateTripType via .partial(), tripTypeQuerySchema, tripTypeIdSchema — tipos exportados con naming PascalCase correcto (TripTypeId, CreateTripType, etc.)",
          "Página /trips/trip-types: buscador, paginación (25 por página), filtro Activos/Eliminados/Todos, Sheet create/edit lateral, Dialog de confirmación delete, restore por fila con onError",
          "Sidebar: entrada 'Tipos de viaje' en sección Viajes",
          "Seeder con 11 registros base: AD (Bolivia), BI (Brasil ida), CH (Chile ida), CV (Chile vuelta), FR (Fripur), IN (Brasil vuelta), NA (Nacional), PR (Paraguay), SA (Salvita), UR (Uruguay), VL (Vueltas varias)",
          "bun run db:seed:trip-types integrado en el pipeline db:seed",
        ],
      },
      {
        title: "Correcciones en TripTypesPage y trips.ts (copy-paste bugs)",
        type: "fix" as const,
        items: [
          "restoreMut: agregado onError: (err) => setFormError(err.message) — los errores de restauración ya no se pierden silenciosamente",
          "Botón editar: oculto cuando row.deletedAt tiene valor — el bloque de acciones ahora muestra solo RestoreIcon en registros eliminados",
          "Mensajes de error copy-paste corregidos: 'Error al cargar tipos de servicio' → '…tipos de viaje'; 'Cargando tipos de servicio…' → '…tipos de viaje'",
          "/trip-types/count: agregado .where(isNull(tripTypes.deletedAt)) — el conteo ya refleja solo registros activos",
          "PATCH /:id (update): select ahora incluye deletedAt; agregada guarda 409 si el registro está soft-deleted",
          "DELETE /:id: logger corregido de 'Service type soft deleted' a 'Trip type soft deleted'",
          "trips.ts schemas: tipo TripTypeIdSchema renombrado a TripTypeId (convención PascalCase sin sufijo Schema); comentarios de sección // ─── TripsTypes y // ─── ServiceTypes corregidos",
        ],
      },
      {
        title: "Docs — Guía CRUD de Entidades",
        type: "feature" as const,
        items: [
          "Checklist actualizado: db:generate solo crea el .sql — sin db:migrate la tabla no existe; agregado paso obligatorio 'Agregar al sidebar'",
          "Nueva sección 7 · Errores típicos: 10 casos documentados con ejemplos ❌/✅ — copy-paste de queryKey/queryFn, botón editar en soft-deleted, restoreMut sin onError, PATCH sin guard, /count sin filtro activos, colisión de tipos TypeScript, comentarios y logs del módulo origen",
          "Nuevo error típico: db:migrate saltea migration silenciosamente cuando el nombre ya existe en __drizzle_migrations (causa: mezcla de db:push y db:generate en dev) — fix documentado: usar db:push para sincronizar el estado real",
        ],
      },
    ],
  },
  {
    version: "v0.8.0",
    date: "2026-05-16",
    sections: [
      {
        title: "Tipos de carga — nueva entidad CRUD completa",
        type: "feature" as const,
        items: [
          "Tabla cargo_types en DB: code varchar(10) UNIQUE, name, deletedAt, timestamps — seeder con 4 tipos base: DE (Devoluciones), LT (LATA), NP (No Perecedero), PE (Perecedero)",
          "API /api/cargos/types: GET (filtro ?deleted), POST, PATCH /:id, DELETE /:id (soft), PATCH /:id/restore — implementado como sub-router encadenado montado con .route('/types', cargoTypesRouter)",
          "Schemas Zod en @cdc-sccr/shared: createCargoTypeSchema (code solo letras A-Z, max 10), updateCargoTypeSchema via .partial(), cargoTypeQuerySchema, cargoTypeIdSchema — con z.preprocess para normalizar a mayúsculas antes de validar",
          "Página /cargos/types: buscador, paginación, filtro Activos/Eliminados/Todos, Sheet create/edit, Dialog de confirmación delete, restore por fila",
          "Menú lateral: entrada 'Tipos de carga' con icono BoxesIcon en la sección Cargas",
          "Script bun run db:seed:cargo-types agregado a db:seed",
        ],
      },
      {
        title: "Manejo de sesión expirada — modal y flujo controlado",
        type: "feature" as const,
        items: [
          "SessionExpiredDialog: modal bloqueante que aparece ante cualquier respuesta 401 en toda la app — muestra mensaje amigable y botón 'Ir al login'",
          "EventTarget global (lib/unauthorized.ts): emitUnauthorized() + subscribeUnauthorized() — bus de eventos sin dependencias externas",
          "useUnauthorizedWatcher hook: limpia la cache de TanStack Query y abre el modal; clearAuth() se difiere hasta que el usuario confirma (evita race con RequireAuth)",
          "authorizedFetch helper: wrapper de fetch con headers automáticos (API key + Bearer + CSRF), manejo centralizado de 401 y errores — reemplaza fetch manual + getHeaders() en toda la app",
          "api (hono/client) ahora usa authorizedFetch como fetch interceptor — todas las llamadas al cliente RPC pasan por la lógica de manejo de errores centralizada",
          "toApiError helper: extrae el campo error del cuerpo JSON antes de lanzar la excepción; el mensaje queda disponible en err.message para onError handlers",
        ],
      },
      {
        title: "Correcciones y ajustes transversales",
        type: "fix" as const,
        items: [
          "Schema/DB mismatch: createCargoSchema.code tenía max(20) pero la columna es varchar(10) — corregido a max(10); el input del formulario también actualizado",
          "Migración SQL 0001: usaba uuid_generate_v7() que no existe en PostgreSQL — corregido a id uuid PRIMARY KEY NOT NULL (Drizzle aplica el default en app)",
          "PATCH /:id en cargo types y cargo products: ahora guarda contra editar registros soft-deleted (409 si deletedAt != null)",
          "Endpoints /count: ahora filtran WHERE deleted_at IS NULL — el conteo refleja solo registros activos",
          "Edit button: oculto cuando row.deletedAt tiene valor; solo se muestra el botón de restaurar para registros eliminados",
          "restoreMut: agregado onError en ambas páginas de cargas para mostrar errores en pantalla; restoringId por fila para no bloquear todos los botones juntos",
          "updateCargoTypeSchema: migrado de definición manual a createCargoTypeSchema.partial() para mantener sincronía automática",
          "Seed cargo-types.ts: corregido cierre de conexión DB en path de error (process.exit(1) salteaba el finally)",
          "handle401 en main.tsx: condición corregida para que TanStack Query actúe como safety net secundario (error.cause?.status === 401)",
        ],
      },
      {
        title: "Tests — Schemas de cargos",
        type: "feature" as const,
        items: [
          "cargos.test.ts ampliado: 14 casos nuevos cubriendo createCargoTypeSchema, updateCargoTypeSchema, cargoTypeQuerySchema — nombre vacío, código vacío, nombre > 200 chars, objeto vacío en update, deleted opcional",
          "createCargoSchema corregido en tests: límite del código ahora es 10 (alineado con la corrección del schema)",
          "Total: 86 tests / 0 fallos en packages/shared",
        ],
      },
    ],
  },
  {
    version: "v0.7.0",
    date: "2026-05-15",
    sections: [
      {
        title: "Soft delete + Restore — Provincias, Ciudades y Rutas",
        type: "feature" as const,
        items: [
          "Soft delete en las tres entidades: DELETE ahora setea deleted_at en vez de eliminar el registro. Incluye guarda de idempotencia (409 si ya estaba eliminado)",
          "Endpoints PATCH /states/:id/restore, /cities/:id/restore y /routes/:id/restore — revierten deleted_at a null",
          "Filtro Activos / Borrados / Todos en provincias, ciudades y rutas — enviado como ?deleted=true|false a la API",
          "Botón verde de restaurar (RestoreIcon) aparece en la columna de acciones cuando el registro está borrado; reemplaza editar/eliminar",
          "CORS: método PATCH agregado a Access-Control-Allow-Methods",
        ],
      },
      {
        title: "Ciudades — Tipo (Ciudad / Trayectoria)",
        type: "feature" as const,
        items: [
          "Columna type (city_type enum: city | trayectory) en tabla cities — NOT NULL con default city",
          "Badge de color en la tabla: azul para ciudades, verde para trayectorias",
          "Filtro de tipo en el toolbar (Todos los tipos / Ciudad / Trayectoria)",
          "Botón 'Crear trayectoria' independiente de 'Nueva ciudad': abre el mismo sheet con type fijo en trayectory",
          "Formulario de ciudad: código logístico ahora obligatorio, campo tipo como readonly (no seleccionable)",
        ],
      },
      {
        title: "Creación de trayectorias — UX autocomplete",
        type: "feature" as const,
        items: [
          "Panel de creación de trayectoria: selección de hasta 10 ciudades activas (no trayectorias) mediante autocomplete con buscador filtrable",
          "Nombre autogenerado en tiempo real: Ciudad1 / Ciudad2 / ... / CiudadN respetando el orden de selección",
          "Código autogenerado (10 chars): distribución equitativa entre ciudades, reparto con remainder a las primeras — ej. 3 ciudades → 4+3+3",
          "Caracteres sanitizados: sin acentos, sin símbolos, solo A-Z0-9, uppercase",
          "Validaciones: mínimo 2 ciudades, máximo 10, sin duplicados; al editar una trayectoria existente nombre/código son editables manualmente",
        ],
      },
      {
        title: "Schemas y validaciones",
        type: "feature" as const,
        items: [
          "states.code: pasó de opcional a obligatorio en createStateSchema — alineado con constraint NOT NULL en DB",
          "cities.code: pasó de opcional (max 20) a obligatorio min 1 max 10 en createCitySchema — alineado con varchar(10) NOT NULL",
          "createCitySchema: nuevo campo type (enum city | trayectory, default city)",
          "updateCitySchema: .partial() — permite editar solo nombre o código sin requerir type",
        ],
      },
      {
        title: "Tests — Primera etapa",
        type: "feature" as const,
        items: [
          "packages/shared/src/utils/trajectory.ts: funciones puras extraídas de CitiesPage — sanitizeForCode, computeAllocations, computeTrajectoryCode, computeTrajectoryName",
          "trajectory.test.ts: 22 tests — distribución de caracteres, sanitización, código y nombre de trayectorias",
          "geography.test.ts: 24 tests — validaciones de createCountrySchema, createStateSchema, createCitySchema, updateCitySchema, createRouteSchema",
          "api.test.ts: 8 smoke tests sin DB — health endpoint, API key middleware, CORS preflight, loginSchema",
          "Scripts bun test / bun test:watch en packages/shared y apps/api; turbo run test en root",
        ],
      },
      {
        title: "Seeders",
        type: "fix" as const,
        items: [
          "states.ts: soporte para flag deleted opcional en la lista STATES — setea deleted_at en el upsert (para estados como NS/No especificado)",
          "cities.ts: eliminada la validación de que la provincia pertenezca al país — se busca el estado solo por código",
        ],
      },
      {
        title: "Otros",
        type: "fix" as const,
        items: [
          "CountriesPage: columna ISO ahora aparece antes que Nombre",
          "geography.ts: migrado de geo.get() separados a cadena de métodos en new Hono() para preservar la inferencia de tipos RPC del cliente",
          "StatesPage: deletedAt incluido en el SELECT de GET /states — necesario para mostrar el botón de restaurar",
        ],
      },
    ],
  },
  {
    version: "v0.6.2",
    date: "2026-05-11",
    sections: [
      {
        title: "Datos crudos y reportes de importación",
        type: "fix" as const,
        items: [
          "Archivos fuente de localidades y trayectos ubicados en apps/api/data/20160511/ (localidades-20260511.txt, trayectos-202600511.txt)",
          "Seeder routes.ts: lectura por defecto desde data/20160511/trayectos-202600511.txt; ruta alternativa como argumento del script",
          `Páginas ${REPORT_GEO_IMPORT_PATH} y /reports/routes-import actualizadas con cifras verificadas tras db:fresh + db:seed (+ db:seed:routes para rutas)`,
          "Reporte de rutas: aclaración de que el detalle vive en consola (no se genera Markdown automático); tabla de códigos de ciudad más frecuentes sin match",
        ],
      },
    ],
    report: { label: "Ver reporte — importación localidades (2026-05-11)", href: REPORT_GEO_IMPORT_PATH },
  },
  {
    version: "v0.6.1",
    date: "2026-05-11",
    sections: [
      {
        title: "Geografía — Seeder de ciudades y nombres",
        type: "fix" as const,
        items: [
          "Seeder cities.ts: eliminadas correcciones manuales de país/estado, alias de provincia (p. ej. RS↔RGS, SCA) y el listado de skips documentados en consola; las filas erróneas quedan como no resueltas hasta corregir fuente o DB",
          "Nombres insertados (ciudades y rutas): formato título por segmentos separados con « / », p. ej. Apostoles / J America / Bs As — helper format-display-name.ts compartido con db:seed:routes",
          `Reporte ${REPORT_GEO_IMPORT_PATH} (ruta fechada desde v0.6.2): describe el comportamiento actual, inconsistencias de fuente sin columna de «corrección» automática, y pendientes`,
          "Ciudades: unicidad por (code, state_id); re-seed idempotente vía onConflict sobre ese par — nombres duplicados con códigos distintos ya no colisionan; en dev seguir usando db:fresh al cambiar el dataset embebido",
        ],
      },
    ],
  },
  {
    version: "v0.6.0",
    date: "2026-05-11",
    sections: [
      {
        title: "Rutas — Schema, API y UI",
        type: "feature" as const,
        items: [
          "Tabla routes: code (UNIQUE), name, origin_city_id, target_city_id — migration 0002",
          "API REST: GET/POST/PUT/DELETE /api/geo/routes — GET con doble JOIN a cities usando alias de drizzle-orm/pg-core",
          "Página CRUD en /geo/routes: buscador por código/nombre/ciudad, paginación, selector cascadeado País → Provincia → Ciudad para origen y destino",
          "Componente CitySelector reutilizable: pre-carga el selector en modo edición",
          "Sidebar Rutas activado (removía badge Soon)",
        ],
      },
      {
        title: "Script de importación de trayectos",
        type: "feature" as const,
        items: [
          "apps/api/src/db/seeds/routes.ts: TSV (trayecto, descrip, origen, destino), resuelve ciudades por código e inserta rutas; archivo por defecto en data/20160511/trayectos-202600511.txt",
          "Idempotente: onConflictDoNothing sobre routes.code",
          "Resumen y códigos no resueltos en consola al ejecutar bun run db:seed:routes; ruta opcional: bun run src/db/seeds/routes.ts <archivo.tsv>",
          "Ver /reports/routes-import para números de referencia y top códigos de ciudad faltantes",
        ],
      },
    ],
  },
  {
    version: "v0.5.0",
    date: "2026-05-11",
    sections: [
      {
        title: "Provincias y Ciudades — Buscador y paginación",
        type: "feature" as const,
        items: [
          "Buscador client-side en Provincias y Ciudades: filtra por nombre y código simultáneamente",
          "Paginación con PAGE_SIZE=25 y componente \u003cPagination\u003e reutilizable (X–Y de Z resultados, prev/next)",
          "Página se resetea automáticamente al cambiar cualquier filtro o búsqueda",
          "Mensaje diferenciado: 'Sin resultados para la búsqueda' vs 'No hay registros'",
        ],
      },
      {
        title: "Ciudades — Mejoras de UI",
        type: "feature" as const,
        items: [
          "Columna Código agregada a la tabla (badge mono, muestra el código logístico)",
          "Columna Cód. postal eliminada de la tabla",
          "Campo Código logístico agregado al formulario de create/edit",
        ],
      },
      {
        title: "Correcciones — Selects y datos",
        type: "fix" as const,
        items: [
          "Selects de país y provincia mostraban UUID en el trigger — corregido calculando el nombre del item seleccionado como children de SelectValue (comportamiento de Base UI)",
          "Seeder de estados simplificado: eliminada lógica de renames/deletions, solo lista limpia con onConflictDoNothing",
          "RGS mantenido como código interno para Rio Grande do Sul (BR)",
          "NS (\"No especificado\") eliminado de Argentina — solo aplica a UY",
        ],
      },
      {
        title: "CLAUDE.md — Patrón estándar de listado",
        type: "feature" as const,
        items: [
          "Documentado el patrón base para toda página de listado CRUD: buscador + paginación + filtros",
          "Código de referencia: PAGE_SIZE, useMemo para filtered/paged, useEffect para reset de página",
          "Convención para SelectValue con nombres calculados en lugar de UUIDs",
          "Acciones: Sheet lateral para create/edit, Dialog centrado para confirmación de delete",
        ],
      },
    ],
  },
  {
    version: "v0.4.0",
    date: "2026-05-11",
    sections: [
      {
        title: "Geografía — Schema y API",
        type: "feature" as const,
        items: [
          "Tablas countries, states y cities con UUID v7 (time-ordered, mejor localidad de índice)",
          "Columna code agregada a states y cities vía migration 0001",
          "API REST: /geo/countries, /geo/states, /geo/cities — GET abierto a autenticados, POST/PUT/DELETE solo admin",
          "JOIN con datos del padre en respuestas de states (countryName, isoCode) y cities (stateName, countryName)",
          "Filtros ?countryId y ?stateId en endpoints de listado",
        ],
      },
      {
        title: "Geografía — Navegación y UI",
        type: "feature" as const,
        items: [
          "Sección 'Geografía' en sidebar visible para admin y operador",
          "Páginas CRUD: Países, Provincias y Ciudades con Sheet lateral (create/edit) y Dialog de confirmación (delete)",
          "Filtros en cascada: países → provincias → ciudades",
          "Ítem 'Rutas' deshabilitado con badge Soon",
        ],
      },
      {
        title: "Seeders — Decisiones de datos",
        type: "fix" as const,
        items: [
          "Estado RIO (AR) renombrado: 'Río Negro' → 'La Rioja'. RIN (AR) agregado para Río Negro",
          "Rio Grande do Sul BR: código interno RGS en DB (convivencia con fuentes que usan RS quedó documentada; desde v0.6.1 el seeder no aliasa RS↔RGS)",
          "Estados nuevos: SJU (San Juan AR), NS (No especificado AR), RON (Rondônia BR), ITA (Itapúa PY), CAU (La Araucanía CL)",
          "71 provincias y ~1560 ciudades/localidades en el primer import; filas vacías o con estado inexistente se omiten o informan como no resueltas",
          "Estados pendientes sin datos suficientes: ALT (CL/PY), MAG/Magallanes (CL), APY/Alto Paraná (PY), POT/Potosí (BO)",
          "Comandos: db:seed:countries, db:seed:states, db:seed:cities, db:seed (todos), db:fresh (reset+push+seed)",
        ],
      },
    ],
  },
  {
    version: "v0.3.0",
    date: "2026-05-08",
    sections: [
      {
        title: "Dashboard — Dominio de transporte",
        type: "feature" as const,
        items: [
          "KPIs adaptados al negocio: Viajes Confirmados, Cargas Confirmadas, Choferes Activos y Crecimiento en Cargas Despachadas",
          "Gráfico renombrado a 'Viajes vs Cargas' — los viajes son siempre mayores que las cargas, reflejando la lógica de negocio",
          "Textos y fechas del gráfico en español (locale es-AR): Últimos 3 meses / Últimos 30 días / Últimos 7 días",
          "Descripción del gráfico dinámica según el período seleccionado",
          "Página de Ayuda actualizada con terminología del dominio (viajes, cargas, choferes, despacho)",
        ],
      },
      {
        title: "Auth y sesión",
        type: "fix" as const,
        items: [
          "initAuth(): restaura sesión completa al recargar la página — lee token y user desde localStorage, recupera csrfToken del payload JWT (sessionId)",
          "Verificación de expiración del JWT al restaurar sesión — limpia localStorage si el token venció",
          "Redirección de usuarios autenticados: / y /login redirigen al dashboard en lugar de mostrar la pantalla de login",
          "RequireAuth: migrado de <Navigate> síncrono a useEffect — elimina el loop infinito de renders al cerrar sesión",
          "CORS: X-CSRF-Token agregado a Access-Control-Allow-Headers; PUT y DELETE agregados a Access-Control-Allow-Methods",
        ],
      },
      {
        title: "Interfaz",
        type: "fix" as const,
        items: [
          "SidebarMenuButton convertido a React.forwardRef — soluciona pantalla en blanco al acceder al dashboard (Base UI inyectaba ref en componente no compatible)",
          "DropdownMenuLabel y DropdownMenuItem envueltos en DropdownMenuGroup — corrige error de contexto de Base UI al abrir el menú del usuario",
          "Eliminados 47 archivos .js compilados que sobreescribían los .tsx originales, evitando que los cambios se reflejaran en pantalla",
        ],
      },
    ],
  },
  {
    version: "v0.2.0",
    date: "2026-05-08",
    sections: [
      {
        title: "Seguridad — API",
        type: "feature" as const,
        items: [
          "Schema migrado: tabla users ampliada con firstName, lastName, isActive, deletedAt, fleetId; nueva tabla fleets; nueva tabla sessions con userId, token, expiresAt, ipAddress, userAgent",
          "Roles actualizados: admin | operator | fleet_manager | driver",
          "Sesiones en DB: el login crea una sesión; requireAuth valida que exista y no esté expirada; el logout la elimina — permite revocar sesiones",
          "Rate limiting: 5 intentos de login por IP cada 15 minutos; responde 429 con header Retry-After",
          "CSRF: el sessionId viaja en el JWT y como csrfToken en la respuesta del login; requests mutantes deben incluir X-CSRF-Token",
          "Secure headers: X-Frame-Options, HSTS, X-Content-Type-Options activados",
          "Logging: se loguean intentos fallidos y logins exitosos con IP y sessionId",
        ],
      },
      {
        title: "Frontend — Web",
        type: "feature" as const,
        items: [
          "Dashboard shadcn/ui con sidebar, 4 indicadores KPI y gráfico de área interactivo",
          "Migración a Tailwind v4 con @tailwindcss/vite plugin",
          "Login conectado al backend: POST /api/auth/login con manejo de errores y estado de carga",
          "Auth store: token en localStorage, csrfToken solo en memoria",
          "Rutas protegidas: RequireAuth redirige a /login si no hay sesión; RequireRole redirige al dashboard si el rol no tiene acceso",
          "Guía de administración (/docs/admin): solo accesible para admin",
          "Guía del operador (/docs/operator): accesible para admin y operator",
          "Layout compartido con sidebar persistente entre páginas",
        ],
      },
    ],
  },
  {
    version: "v0.1.0",
    date: "2026-05-07",
    sections: [
      {
        title: "Scaffold inicial",
        type: "feature" as const,
        items: [
          "Monorepo Turborepo + Bun workspaces",
          "API Hono v4 con RPC nativo",
          "ORM Drizzle + PostgreSQL",
          "Auth JWT stateless (jose)",
          "SSE broker in-memory",
          "Frontend React 18 + Vite + shadcn/ui",
        ],
      },
    ],
  },
]

export default function ChangelogPage() {
  return (
    <>
      <SiteHeader title="Changelog" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="max-w-3xl space-y-10">
          {entries.map((entry) => (
            <div key={entry.version}>
              {/* Version header */}
              <div className="flex items-center gap-3 mb-6">
                <span className="text-lg font-bold">{entry.version}</span>
                <span className="text-xs font-mono bg-muted px-2 py-1 rounded text-muted-foreground">
                  {entry.date}
                </span>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Link to the report, if available */}
              {'report' in entry && entry.report && (
                <div className="mb-4 pl-1">
                  <Link
                    to={entry.report.href}
                    className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <FileTextIcon className="size-3" />
                    {entry.report.label}
                  </Link>
                </div>
              )}

              {/* Secciones */}
              <div className="space-y-6 pl-1">
                {entry.sections.map((section) => (
                  <div key={section.title}>
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="outline" className="gap-1 text-xs">
                          {section.type === "feature"
                            ? <TrendingUpIcon className="size-3" />
                            : <WrenchIcon className="size-3" />}
                          {section.type === "feature" ? "nuevo" : "fix"}
                        </Badge>
                      <h3 className="text-sm font-semibold">{section.title}</h3>
                    </div>
                    <ul className="space-y-2 pl-2 border-l-2 border-border ml-1">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex gap-2 text-sm text-muted-foreground pl-3">
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-muted-foreground/50 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
